import { sha256 as nobleSha256 } from '@noble/hashes/sha2'
import { hkdf as nobleHkdf } from '@noble/hashes/hkdf'
import { CommitPipeline, type MutableFileEntry, type MutationOp } from './commitPipeline'
import { computeLineMapping } from './diff'
import type {
    EngineManager,
    EngineManagerEventPayload,
    EngineManagerEventType,
    BlameLine,
    CommitSummary,
    DiffHunk,
    FileSystem,
    FileStat,
    History,
    IdentityProvider,
    LocalStore,
    McpConnectionEntry,
    ProfileEntry,
    RemoteStore,
    Sharing,
    SyncState,
    SyncProgress,
    SyncLogEntry,
    WorkspaceSyncPolicy,
    Workspace,
    WorkspaceEntry,
    WorkspaceEventPayload,
    WorkspaceEventType,
    WorkspaceInfo,
    WorkspaceResourceLease,
    ContentRef,
    UserIdentity,
    EngineError,
    EngineErrorCode,
    PresenceManager,
    PresenceEntry,
    WriterInfo,
    ImportFileEntry,
    ImportFilesOptions,
    ImportFilesResult,
    ReadTextOptions,
} from './types'
import { EngineException } from './types'
import { decryptState, type WorkspaceState, type WriterStateEntry } from './codec/state'
import { flattenStateToFiles, buildTreeFromPaths, collectTreeHashes, createDirObject, encryptDirObject, diffMerkleTrees, type FlatFileEntry, type DirObjectStore, type DirTreeCodecDeps, type DirEntry, type DirEntryFile, type DirEntryDir, type DirEntrySymlink } from './codec/dirTree'
import { DirTreeCache } from './dirTreeCache'
import { IdbLocalStore, MemoryLocalStore } from './localStore'
import { DebounceSyncScheduler, HttpRemoteStore, WebCryptoProvider } from './providers'
import { WsNotificationClient } from './wsNotificationClient'
import type {
    SqliteEngineFactory,
    WorkspaceSqliteEngineFactoryContext,
    WorkspaceSqliteEngineFactoryProvider,
    Sqlite3MtOptions,
} from './sqliteEngine'
import { initSqlite3 } from './sqlite3-wasm-loader'
import { createSqlite3MtWorkspaceProvider } from './workspaceSqlite3MtProvider'
import { InMemoryDurableLocalStore } from './sqlite3mt/durable'
import { IdbDurableLocalStore, buildSqlite3MtDurableIdbName } from './sqlite3mt/durable-idb'
import type { Sqlite3WasmRuntimeApi } from './sqlite3mt/engine-factory'
import { sdkConsole, type SdkLoggingOptions } from './logger'

export class EventHub<TPayloadMap extends Record<string, unknown>> {
    private listeners = new Map<keyof TPayloadMap, Set<(payload: any) => void>>()
    on<E extends keyof TPayloadMap>(event: E, fn: (payload: TPayloadMap[E]) => void): () => void {
        const current = this.listeners.get(event) ?? new Set()
        current.add(fn as (payload: any) => void)
        this.listeners.set(event, current)
        return () => this.off(event, fn)
    }
    off<E extends keyof TPayloadMap>(event: E, fn: (payload: TPayloadMap[E]) => void): void {
        this.listeners.get(event)?.delete(fn as (payload: any) => void)
    }
    emit<E extends keyof TPayloadMap>(event: E, payload: TPayloadMap[E]): void {
        this.listeners.get(event)?.forEach(listener => listener(payload))
    }
}


import { getReadKey, getWriteSeed, redactCapability } from './capabilityUtils'
import { getCrossTabChannel } from './crossTabChannel'

export function shouldUseVirtualSqlite3MtRuntimeOwner(): boolean {
    const runtimeGlobal = globalThis as typeof globalThis & {
        process?: {
            versions?: {
                node?: string
            }
        }
        __secureblobComponentJobRuntimeEnv?: unknown
    }

    const hasNodeProcess = typeof runtimeGlobal.process?.versions?.node === 'string'
    const hasComponentJobRuntime =
        typeof runtimeGlobal.__secureblobComponentJobRuntimeEnv === 'object' &&
        runtimeGlobal.__secureblobComponentJobRuntimeEnv !== null

    return hasNodeProcess || hasComponentJobRuntime
}

const SHA256_HEX_RE = /^[a-f0-9]{64}$/i

function normalizeObjectHash(hash: string | null | undefined): string | null {
    return typeof hash === 'string' && SHA256_HEX_RE.test(hash) ? hash.toLowerCase() : null
}

function addReferencedObjectHash(refs: Set<string>, hash: string | null | undefined): void {
    const normalized = normalizeObjectHash(hash)
    if (normalized) refs.add(normalized)
}

function deriveWorkspaceKey(capability: string, blobId: string, scope: 'state' | 'file'): Uint8Array {
    const r = getReadKey(capability) ?? capability
    const readKeyBytes = base64UrlDecodeBytes(r)
    const salt = new TextEncoder().encode(`secureblob:${blobId}`)
    const info = new TextEncoder().encode(`secureblob-${scope}-v1`)
    // Use @noble/hashes HKDF (RFC 5869) — matches C# SDK's CryptoProvider.HkdfDerive
    return nobleHkdf(nobleSha256, readKeyBytes, salt, info, 32)
}

function base64UrlDecodeBytes(str: string): Uint8Array {
    let base64 = str.replace(/-/g, '+').replace(/_/g, '/')
    while (base64.length % 4) base64 += '='
    const binary = atob(base64) // throws on invalid base64 — intentional fail-fast
    const bytes = new Uint8Array(binary.length)
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
    return bytes
}

import { BlobRepository, BlobRepositoryImpl } from './blobRepository'
import { IdbStoreBackend, MemoryStoreBackend, HttpStoreBackend, ensurePersistentBrowserStorage } from './storeBackends'
import { computeTreeMerge, type TreeMergeIO, type MergeConflictPolicy } from './treeMerge'
import { PageChangeTracker, type TransactionMode } from './pageChangeTracker'
import { bootstrapWorkspace, createWorkspaceAuthProvider, createWorkspaceInviteV2 } from './workspaceAuth'
import type { WorkspaceAuthProvider } from './workspaceSecretV2'
import type { BrowserSqliteOpenConfig } from './browserSqliteProtocol'
import { buildBrowserSqliteHeadBlobId, buildBrowserSqliteHeadLane } from './browserSqliteProtocol'
import { decodeWorkspaceSecretV2 } from './workspaceSecretV2'
import { emitSdkEvent, emitSdkMetric, type SdkObservers } from './observers'

/** Extract the CAS hash from a ContentRef (inline → hash, chunked → manifestHash). */
function getContentHash(ref: ContentRef): string {
    return ref.type === 'inline' ? ref.hash : ref.manifestHash
}

function normalizeDirPath(path: string): string {
    if (!path || path === '/') return '/'
    const normalized = path.replace(/\\/g, '/').replace(/\/+$/, '')
    return normalized.startsWith('/') ? normalized : `/${normalized}`
}

function parentDirPath(path: string): string {
    const normalized = normalizeDirPath(path)
    const withoutTrailingSlash = normalized === '/' ? normalized : normalized.replace(/\/+$/, '')
    const idx = withoutTrailingSlash.lastIndexOf('/')
    return idx <= 0 ? '/' : withoutTrailingSlash.slice(0, idx)
}

function joinDirEntryPath(dirPath: string, entry: DirEntry): string {
    const base = normalizeDirPath(dirPath)
    const child = base === '/' ? `/${entry.name}` : `${base}/${entry.name}`
    return entry.kind === 'directory' ? `${child}/` : child
}

function mutableFromDirEntry(path: string, entry: DirEntry): MutableFileEntry {
    if (entry.kind === 'file') {
        const file = entry as DirEntryFile
        return {
            path,
            kind: 'file',
            mime: file.mime,
            size: file.size,
            mtime: file.mtime,
            ctime: file.ctime,
            content: file.content,
            lastWriterKid: file.lastWriterKid,
            meta: file.meta,
        }
    }

    if (entry.kind === 'directory') {
        const dir = entry as DirEntryDir
        return {
            path: path.endsWith('/') ? path : `${path}/`,
            kind: 'directory',
            mime: '',
            size: 0,
            mtime: dir.mtime,
            ctime: dir.ctime,
            dirHash: dir.dirHash,
            content: { type: 'inline', hash: '' },
            meta: dir.meta,
        }
    }

    const symlink = entry as DirEntrySymlink
    return {
        path,
        kind: 'symlink',
        mime: '',
        size: 0,
        mtime: symlink.mtime,
        ctime: symlink.ctime,
        target: symlink.target,
        content: { type: 'inline', hash: '' },
        lastWriterKid: symlink.lastWriterKid,
        meta: symlink.meta,
    }
}


interface RealWorkspaceDeps {
    entry: WorkspaceEntry
    repository: BlobRepository
    authProvider?: WorkspaceAuthProvider
    /** M1: Optional reference to local store for proactive GC */
    localBackend?: { runGC(maxObjects?: number, protectedHashes?: Set<string>): Promise<number> }
    /** §3.1.1: Identity provider for resolving self writer name/emoji */
    identityProvider?: { getIdentity(): { kid: string; name: string; emoji: string } | null }
    /**
     * Merge conflict resolution policy for workspace file merges.
     * Controls how text and binary file conflicts are resolved.
     * @default { text: 'remote_wins', binary: 'remote_wins' }
     */
    mergeConflictPolicy?: MergeConflictPolicy
    createSqliteEngineFactory?:
        WorkspaceSqliteEngineFactoryProvider['createSqliteEngineFactory']
    /**
     * sqlite3mt multi-worker SQLite configuration.
     *
     * By default (undefined), sqlite3mt is the production SQLite engine.
     * Pass an options object to customize page size, persistence mode, etc.
     */
    useSqlite3Mt?: Sqlite3MtOptions
    observers?: SdkObservers
    logging?: SdkLoggingOptions
}

interface SnapshotFile {
    path: string
    kind: 'file' | 'directory' | 'symlink'
    mime: string
    size: number
    mtime: number
    ctime: number
    lastWriterKid?: string
    target?: string
    content: MutableFileEntry['content']
    meta?: Record<string, unknown>
}

interface ReplayPlan {
    baseHead: string | null
    commits: string[]
}

type SyncResolution =
    | { kind: 'none'; touchedConflictState: boolean }
    | { kind: 'replay'; replay: ReplayPlan; touchedConflictState: boolean }

type HeadRelation =
    | { kind: 'same'; mergeBase: string }
    | { kind: 'local_ahead'; mergeBase: string; replayCommits: string[] }
    | { kind: 'remote_ahead'; mergeBase: string }
    | { kind: 'diverged'; mergeBase: string | null }

export class RealWorkspace implements Workspace {
    readonly blobId: string
    readonly canWrite: boolean
    label: string
    emoji: string

    private readonly events = new EventHub<WorkspaceEventPayload>()
    private readonly scheduler = new DebounceSyncScheduler()
    private readonly crypto = new WebCryptoProvider()
    private readonly files = new Map<string, MutableFileEntry>()
    private readonly pipeline: CommitPipeline
    private readonly stateKey: Uint8Array
    private readonly commitSnapshots = new Map<string, Map<string, SnapshotFile>>()
    private historyIndex: CommitSummary[] = []
    private readonly pendingTextCache = new Map<string, string>()
    /** §7.5.2: Per-session in-memory LRU cache for readObject results */
    private readonly objectCache = new Map<string, Uint8Array>()
    private static readonly OBJECT_CACHE_MAX = 400
    /** Immutable head → referenced object hashes; avoids repeat manifest/tree walks on CAS retry. */
    private readonly referencedObjectsCache = new Map<string, string[]>()
    private static readonly REFERENCED_OBJECTS_CACHE_MAX = 64
    /** Writer kid derived from seed — set during initialize() */
    private derivedWriterKid: string = 'anonymous'
    /** Deduplicate ACL bootstrap when a fresh workspace races multiple auth-guarded requests. */
    private bootstrapPromise: Promise<void> | null = null

    // §18 Writer management — accumulated from commit history + state metadata
    private readonly knownWriters = new Map<string, WriterInfo>()
    /** Writer metadata from state (persisted across syncs) */
    private writerStateEntries: WriterStateEntry[] = []

    // §17 Presence — heuristic: self + recent file writers
    private readonly presenceEvents = new EventHub<{ 'presence:changed': PresenceEntry[] }>()
    private heartbeatTimer: ReturnType<typeof setInterval> | null = null
    private currentActiveFile: string | undefined

    /** Public accessor for derived kid — used by RealEngineManager.lookupKid */
    get derivedKid(): string { return this.derivedWriterKid }

    /**
     * §96 sqlite3mt integration flag.
     * sqlite3mt is the default. When active, session.openSqlite() bypasses the legacy
     * page-level sync wiring because sqlite3mt's control worker owns the
     * full sync lifecycle internally.
     */
    get useSqlite3Mt(): Sqlite3MtOptions | undefined {
        const value = this.deps.useSqlite3Mt
        return value != null && typeof value === 'object' ? value : undefined
    }
    private readonly syncLog: SyncLogEntry[] = []
    private syncLogCounter = 0
    private mutationQueue: Promise<void> = Promise.resolve()
    private queuedMutationCount = 0
    private syncing = false
    private syncRequestedWhileBusy = false
    private queuedPeerLocalHead: string | null = null
    private syncMode: 'active' | 'suspended' = 'active'
    private committing = false
    private importing = false
    private tombstoned = false
    private lastSyncAt = 0
    private lastSyncError: SyncState['lastSyncError'] = undefined
    private lastConflictPaths: string[] = []
    // §7.8: Sync progress & control
    private syncAbortController: AbortController | null = null
    private syncPolicy: WorkspaceSyncPolicy = 'on-demand'
    private syncPaused = false
    private syncProgressData: SyncProgress | undefined = undefined
    private readonly handleOnline: () => void
    private readonly handleOffline: () => void
    private readonly crossTabUnsub: () => void
    // §94 §7: Resource-scoped lease state
    private pendingSqliteModes = new Map<string, TransactionMode>()
    private activeLease: { leaseId: string; resourceId: string; mode: 0 | 1 | 2 } | null = null
    private leaseHeartbeatTimer: ReturnType<typeof setInterval> | null = null
    private closed = false

    /**
     * §96 sqlite3mt: Registry of headBlobId → sync-trigger callbacks.
     * Populated by the sqlite3mt provider when each database's sync engine
     * becomes ready. Used by RealEngineManager to route WS head-change
     * notifications to the correct database's sync engine.
     */
    private readonly sqlite3mtSyncCallbacks = new Map<string, () => void>()
    private readonly pendingSqlite3MtSyncHeadIds = new Set<string>()
    private readonly sqlite3mtHeadListeners = new Set<(headBlobId: string) => void>()
    private readonly sqlite3mtHeadUnregisteredListeners = new Set<(headBlobId: string) => void>()
    private readonly sqlite3mtHeadSubscriptions = new Map<string, { blobId: string; lane: string }>()
    private readonly sqlite3mtLaneToHeadBlobId = new Map<string, string>()
    private notificationClient: WsNotificationClient | null = null

    constructor(private readonly deps: RealWorkspaceDeps) {
        this.blobId = deps.entry.blobId
        this.label = deps.entry.label
        this.emoji = deps.entry.emoji
        this.canWrite = deps.entry.canWrite ?? true

        const stateKey = deriveWorkspaceKey(deps.entry.capability, deps.entry.blobId, 'state')
        const fileKey = deriveWorkspaceKey(deps.entry.capability, deps.entry.blobId, 'file')
        this.stateKey = stateKey

        const writeSeedStr = getWriteSeed(deps.entry.capability)
        let writerSeed: Uint8Array | undefined
        if (writeSeedStr) {
            try {
                // If it's a seed (32 bytes), it should be base64url or raw hex? 
                // Spec says capability 'w' field is base64url.
                writerSeed = base64UrlDecodeBytes(writeSeedStr)
            } catch (e) {
                sdkConsole.warn('Failed to decode write seed', e)
            }
        }

        if (!writerSeed || writerSeed.length !== 32) {
            if (this.canWrite) {
                sdkConsole.error(`[RealWorkspace] Write capability requested but no valid write seed found. Demoting to Read-Only. SeedStr: ${writeSeedStr?.slice(0, 5)}... (${writeSeedStr?.length}), DecodedLen: ${writerSeed?.length}`)
                this.emitSdkObserverEvent('workspace', 'warn', 'Workspace write capability was demoted to read-only.', {
                    blobId: this.blobId,
                    hadWriteCapability: true,
                })
            }
            this.canWrite = false
            writerSeed = undefined
        }

        this.pipeline = new CommitPipeline({
            blobId: this.blobId,
            writerSeed,
            repository: deps.repository,
            crypto: this.crypto,
            scheduler: this.scheduler,
            stateKey,
            fileKey,
        })
        this.refreshState()

        // §20 §7.6 — instant sync on offline→online transition
        this.handleOnline = () => {
            this.refreshState()
            this.events.emit('state:changed', this.state)
            if (this.syncPolicy !== 'auto') return
            void this.syncNowInternal({ preferLazyRemoteAdoption: true }).catch(err => sdkConsole.warn('Online-triggered sync failed', err))
        }
        this.handleOffline = () => {
            this.refreshState()
            this.events.emit('state:changed', this.state)
        }
        if (typeof window !== 'undefined') {
            window.addEventListener('online', this.handleOnline)
            window.addEventListener('offline', this.handleOffline)
        }

        // T2: Listen for commits from other tabs → trigger sync
        this.crossTabUnsub = getCrossTabChannel().onMessage((msg) => {
            if (msg.type === 'commit' && msg.blobId === this.blobId) {
                void this.handlePeerLocalCommit(msg.head).catch(err => {
                    sdkConsole.warn('Cross-tab peer commit handling failed', err)
                    if (this.syncPolicy !== 'auto') return
                    void this.syncNowInternal({ preferLazyRemoteAdoption: true }).catch(syncErr => sdkConsole.warn('Cross-tab sync failed', syncErr))
                })
            }
        })

        this.initializeRealtimeNotifications()
    }

    getAuthProvider(): WorkspaceAuthProvider | undefined {
        return this.deps.authProvider
    }

    createBrowserSqliteOpenConfig(path: string): BrowserSqliteOpenConfig {
        if (!decodeWorkspaceSecretV2(this.deps.entry.capability)) {
            throw new Error('Browser sqlite worker requires workspace-secret-v2; UI-thread fallback is forbidden.')
        }

        return {
            apiBaseUrl: this.deps.entry.apiBaseUrl,
            blobId: this.blobId,
            workspaceSecret: this.deps.entry.capability,
            label: this.label,
            emoji: this.emoji,
            canWrite: this.canWrite,
            path,
            useSqlite3Mt: this.useSqlite3Mt,
            syncPolicy: this.syncPolicy,
            logging: this.deps.logging,
        }
    }

    getSqlite3MtHeadBlobIds(): readonly string[] {
        return [...this.sqlite3mtSyncCallbacks.keys()]
    }

    onSqlite3MtHeadRegistered(listener: (headBlobId: string) => void): () => void {
        this.sqlite3mtHeadListeners.add(listener)
        return () => this.sqlite3mtHeadListeners.delete(listener)
    }

    onSqlite3MtHeadUnregistered(listener: (headBlobId: string) => void): () => void {
        this.sqlite3mtHeadUnregisteredListeners.add(listener)
        return () => this.sqlite3mtHeadUnregisteredListeners.delete(listener)
    }

    private initializeRealtimeNotifications(): void {
        if (typeof WebSocket === 'undefined' || this.deps.authProvider == null) {
            return
        }
        if (this.notificationClient != null) {
            return
        }

        this.notificationClient = new WsNotificationClient(
            this.deps.entry.apiBaseUrl,
            {
                onConnectionChange: connected => {
                    this.setNotificationSource(connected ? 'websocket' : 'polling')
                },
                onHeadChanged: (blobId, head, lane) => {
                    if (this.closed || this.tombstoned || blobId !== this.blobId) {
                        return
                    }

                    if (lane != null) {
                        const headBlobId = this.sqlite3mtLaneToHeadBlobId.get(this.sqlite3mtLaneSubscriptionKey(blobId, lane))
                        if (headBlobId != null) {
                            sdkConsole.debug('[Sync] sqlite3mt websocket head-changed', {
                                blobId,
                                lane,
                                headBlobId,
                                head: head.slice(0, 12),
                            })
                            this.triggerSqlite3MtSync(headBlobId)
                        }
                        return
                    }

                    if (!this.isOwnPipelineHead(head)) {
                        this.triggerSync()
                    }
                },
            },
            blobId => blobId === this.blobId ? this.deps.authProvider : undefined,
        )
        this.notificationClient.subscribe(this.blobId)
        this.notificationClient.connect()
    }

    private sqlite3mtLaneSubscriptionKey(blobId: string, lane: string): string {
        return `${blobId}\0${lane}`
    }

    private parseSqlite3MtHeadBlobId(headBlobId: string): { blobId: string; lane: string } | null {
        const prefix = `${this.blobId}:sqlite3mt:`
        if (!headBlobId.startsWith(prefix)) {
            return null
        }
        const lane = headBlobId.slice(prefix.length)
        return lane.length > 0 ? { blobId: this.blobId, lane } : null
    }

    private subscribeSqlite3MtHeadNotifications(headBlobId: string): void {
        const target = this.parseSqlite3MtHeadBlobId(headBlobId)
        if (target == null) {
            return
        }
        this.sqlite3mtHeadSubscriptions.set(headBlobId, target)
        this.sqlite3mtLaneToHeadBlobId.set(this.sqlite3mtLaneSubscriptionKey(target.blobId, target.lane), headBlobId)
        this.initializeRealtimeNotifications()
        this.notificationClient?.subscribe(target.blobId, target.lane)
    }

    private unsubscribeSqlite3MtHeadNotifications(headBlobId: string): void {
        const target = this.sqlite3mtHeadSubscriptions.get(headBlobId)
        if (target == null) {
            return
        }
        this.sqlite3mtHeadSubscriptions.delete(headBlobId)
        this.sqlite3mtLaneToHeadBlobId.delete(this.sqlite3mtLaneSubscriptionKey(target.blobId, target.lane))
        this.notificationClient?.unsubscribe(target.blobId, target.lane)
    }

    registerSqlite3MtSyncCallback(
        headBlobId: string,
        triggerSync: () => void,
        options?: { backgroundSync?: 'workspace-policy' | 'manual' },
    ): () => void {
        if (this.closed) {
            return () => {}
        }

        const isNewHead = !this.sqlite3mtSyncCallbacks.has(headBlobId)
        this.sqlite3mtSyncCallbacks.set(headBlobId, triggerSync)
        if (isNewHead) {
            this.subscribeSqlite3MtHeadNotifications(headBlobId)
            this.sqlite3mtHeadListeners.forEach(listener => listener(headBlobId))
        }
        const followsWorkspacePolicy = options?.backgroundSync !== 'manual'
        if (!followsWorkspacePolicy || this.syncPolicy !== 'auto') {
            this.pendingSqlite3MtSyncHeadIds.add(headBlobId)
        } else {
            queueMicrotask(() => {
                if (this.closed || this.tombstoned || this.syncPolicy !== 'auto') return
                this.sqlite3mtSyncCallbacks.get(headBlobId)?.()
            })
        }

        return () => {
            const current = this.sqlite3mtSyncCallbacks.get(headBlobId)
            if (current !== triggerSync) {
                return
            }

            this.sqlite3mtSyncCallbacks.delete(headBlobId)
            this.pendingSqlite3MtSyncHeadIds.delete(headBlobId)
            this.unsubscribeSqlite3MtHeadNotifications(headBlobId)
            this.sqlite3mtHeadUnregisteredListeners.forEach(listener => listener(headBlobId))
        }
    }

    async initialize(): Promise<void> {
        const startedAt = Date.now()
        // Derive writer kid from seed (cached in pipeline)
        const kid = await this.pipeline.getWriterKid()
        if (kid) this.derivedWriterKid = kid

        // Load persisted heads
        const localHeads = await this.deps.repository.getLocalHeads(this.blobId)
        const remoteHead = await this.deps.repository.getRemoteHead(this.blobId)

        this.localHead = localHeads[0]
        this.remoteHead = remoteHead
        sdkConsole.debug(`[Sync] initialize blob=${this.blobId.slice(0, 12)} localHead=${this.localHead?.slice(0, 8) ?? 'none'} remoteHead=${remoteHead?.slice(0, 8) ?? 'none'}`)

        if (this.localHead) {
            // Load state from local head
            try {
                const commitBytes = await this.deps.repository.load(this.localHead)
                if (commitBytes) {
                    const commit = JSON.parse(new TextDecoder().decode(commitBytes))
                    const stateHash = commit.payload?.stateHash || commit.stateHash
                    this.localHeadStateHash = stateHash
                    const stateBytes = await this.deps.repository.load(stateHash)
                    if (stateBytes) {
                        const state = await decryptState(stateBytes, { crypto: this.crypto, stateKey: this.stateKey, blobId: this.blobId })
                        this.files.clear()
                        // §7.2.3: Initialize root hash for incremental commits.
                        // In on-demand mode a reload must not flatten the full
                        // workspace tree. Keep the root as a lazy read surface;
                        // explicit syncNow() still materializes via
                        // ensureExplicitSyncSnapshot().
                        this.currentRootDirHash = state.rootDirHash ?? null
                        this.resetDirTreeCache(this.currentRootDirHash)
                        if (this.syncPolicy !== 'on-demand' || !state.rootDirHash) {
                            const storeAdapter = this.createDirObjectStore()
                            const dirDeps = { crypto: this.crypto, stateKey: this.stateKey, blobId: this.blobId }
                            const flatFiles = await flattenStateToFiles(state.rootDirHash, storeAdapter, dirDeps)
                            for (const file of flatFiles) {
                                this.files.set(file.path, { ...file })
                            }
                        }
                        // Capture the materialized baseline. Lazy/on-demand
                        // starts empty; individual read-materialized entries
                        // are added to the snapshot as they are page-faulted.
                        this.commitSnapshots.set(this.localHead, this.captureFileSnapshot())
                        // Restore persisted writer metadata
                        if (state.writers) {
                            this.writerStateEntries = [...state.writers]
                        }
                    }
                    // Extract writers from commit record
                    this.extractWritersFromCommit(commit)
                }
            } catch (error) {
                sdkConsole.warn('Failed to load persisted local state', error)
                this.emitSdkObserverEvent('workspace', 'warn', 'Failed to load persisted local workspace state.', {
                    blobId: this.blobId,
                    error: error instanceof Error ? error.message : String(error),
                })
            }
        }

        // Ensure self is always in knownWriters
        this.ensureSelfWriter()

        this.refreshState()
        this.emitSdkObserverEvent('workspace', 'info', 'Workspace initialized.', {
            blobId: this.blobId,
            canWrite: this.canWrite,
            localHead: this.localHead ?? null,
            remoteHead: this.remoteHead ?? null,
        })
        this.emitSdkObserverMetric(
            'sdk.workspace.initialize.duration_ms',
            Math.max(0, Date.now() - startedAt),
            {
                blobId: this.blobId,
                canWrite: this.canWrite ? 'true' : 'false',
            },
        )
        void this.syncNowInternal({ preferLazyRemoteAdoption: true }).catch(error => {
            sdkConsole.warn('Initial workspace sync failed', error)
            this.emitSdkObserverEvent('workspace', 'warn', 'Initial workspace sync failed.', {
                blobId: this.blobId,
                error: error instanceof Error ? error.message : String(error),
            })
        })
    }

    state: SyncState = { syncing: false, online: true, connectivity: 'connected', synced: true, lastSyncAt: 0, pendingChanges: 0, syncLog: [], conflictPaths: [], localHead: undefined, remoteHead: undefined, syncPolicy: 'on-demand' }

    private pendingChanges = 0
    private localHead: string | undefined
    /** The parents array from the local head commit — used as CAS base in syncNow */
    private localHeadParents: string[] = []
    /** §7.4: Encrypted state object hash for current localHead — used by GC protection + referencedObjects */
    private localHeadStateHash: string | undefined
    private remoteHead: string | undefined
    /** §7.2.3: Current Merkle root hash — tracked for incremental commits. */
    private currentRootDirHash: string | null = null
    /** Lazy Merkle directory cache for on-demand tree browsing without full flatten. */
    private dirTreeCache: DirTreeCache | null = null
    /** §7.4.5: Max commitSnapshots LRU entries (Engine Spec) */
    private static readonly COMMIT_SNAPSHOTS_MAX = 20

    fs: FileSystem = {
        list: (dirPath?: string): FileStat[] => {
            const prefix = (dirPath && dirPath !== '/') ? dirPath : ''
            return [...this.files.values()]
                .filter(file => !prefix || file.path.startsWith(`${prefix}/`) || file.path === prefix)
                .sort((a, b) => a.path.localeCompare(b.path))
                .map(file => ({ ...file }))
        },
        stat: (path: string): FileStat | null => {
            const file = this.files.get(path)
            return file ? { ...file } : null
        },
        readText: async (path: string, opts?: ReadTextOptions): Promise<string | null> => {
            // Return pending (not-yet-encrypted) text immediately if available
            const pending = this.pendingTextCache.get(path)
            if (pending !== undefined) return pending
            // Race mutation queue against a short timeout to avoid hanging
            // when a commit push is stuck on degraded network
            await Promise.race([this.mutationQueue, new Promise<void>(r => setTimeout(r, 200))])
            await this.ensureDemandReadableRemoteState()
            const file = await this.materializeFileEntry(path)
            if (!file) return null
            const bytes = await this.pipeline.readContent(file.content)
            return bytes ? new TextDecoder(opts?.charset ?? 'utf-8').decode(bytes) : null
        },
        readBytes: async (path: string, opts: { offset: number; length: number }): Promise<Uint8Array | null> => {
            // Race mutation queue against a short timeout to avoid hanging
            // when a commit push is stuck on degraded network
            await Promise.race([this.mutationQueue, new Promise<void>(r => setTimeout(r, 200))])
            await this.ensureDemandReadableRemoteState()
            const file = await this.materializeFileEntry(path)
            if (!file) return null
            // v3 optimization: only fetch chunks that overlap [offset, offset+length)
            return this.pipeline.readContentRange(file.content, Math.max(0, opts.offset), Math.max(0, opts.length))
        },
        createByteStream: async function* (this: RealWorkspace, path: string, opts?: { offset?: number; length?: number; chunkSize?: number }) {
            await this.ensureDemandReadableRemoteState()
            const file = await this.materializeFileEntry(path)
            if (!file) return

            // v3: use streamContentRange for O(1) seek when offset is provided
            const offset = opts?.offset ?? 0
            const length = opts?.length
            const source = (offset > 0 || length !== undefined)
                ? this.pipeline.streamContentRange(file.content, offset, length)
                : this.pipeline.streamContent(file.content)

            for await (const chunk of source) {
                // If caller wants smaller chunks, re-slice; otherwise yield as-is
                const maxSize = opts?.chunkSize ?? chunk.length
                if (chunk.length <= maxSize) {
                    yield chunk
                } else {
                    for (let off = 0; off < chunk.length; off += maxSize) {
                        yield chunk.slice(off, off + maxSize)
                    }
                }
            }
        }.bind(this),
        readBinary: (path: string): Promise<Uint8Array | null> => this.fs.readBytes(path, { offset: 0, length: Number.MAX_SAFE_INTEGER }),
        writeText: (path: string, content: string): void => {
            this.ensureWriteAccess()
            const existing = this.files.get(path)
            if (existing?.kind === 'directory') throw new Error('EISDIR: Is a directory')

            // Optimistic: add file entry + cache text so UI can list/read immediately
            const bytes = new TextEncoder().encode(content)
            this.pendingTextCache.set(path, content)
            this.files.set(path, {
                ...(existing ?? {}),
                path,
                kind: 'file' as const,
                mime: 'text/plain',
                size: bytes.byteLength,
                mtime: Date.now(),
                ctime: existing?.ctime ?? Date.now(),
                lastWriterKid: this.derivedWriterKid,
                content: existing?.content ?? { type: 'inline', hash: '' },
            })
            this.events.emit('fs:changed', { paths: [path] })
            this.enqueueMutation(() => this.write(path, bytes, 'text/plain'))
        },
        writeBytes: (path: string, bytes: Uint8Array): void => {
            this.ensureWriteAccess()
            if (this.files.get(path)?.kind === 'directory') throw new Error('EISDIR: Is a directory')
            this.enqueueMutation(() => this.write(path, bytes, 'application/octet-stream'))
        },
        patchBytes: async (path: string, offset: number, data: Uint8Array): Promise<void> => {
            this.ensureWriteAccess()
            const existing = await this.materializeFileEntry(path)
            if (existing?.kind === 'directory') throw new Error('EISDIR: Is a directory')

            // Read current content, splice in patch, write back.
            // CDC dedup ensures unchanged chunks are reused (not re-encrypted).
            const current = await this.pipeline.readContent(
                this.files.get(path)?.content ?? { type: 'inline', hash: '' },
            )
            if (!current) throw new Error(`patchBytes: file not found: ${path}`)

            const patched = new Uint8Array(Math.max(current.length, offset + data.length))
            patched.set(current)
            patched.set(data, offset)

            this.enqueueMutation(() => this.write(path, patched, this.files.get(path)?.mime ?? 'application/octet-stream'))
        },
        appendBytes: async (path: string, data: Uint8Array): Promise<void> => {
            this.ensureWriteAccess()
            const existing = await this.materializeFileEntry(path)
            if (existing?.kind === 'directory') throw new Error('EISDIR: Is a directory')

            // Read current content, append data, write back.
            // CDC chunk stability means existing chunks are reused via content-addressed dedup.
            const current = await this.pipeline.readContent(
                this.files.get(path)?.content ?? { type: 'inline', hash: '' },
            )

            let combined: Uint8Array
            if (current) {
                combined = new Uint8Array(current.length + data.length)
                combined.set(current)
                combined.set(data, current.length)
            } else {
                combined = data
            }

            this.enqueueMutation(() => this.write(path, combined, this.files.get(path)?.mime ?? 'application/octet-stream'))
        },
        delete: (path: string): boolean => {
            this.ensureWriteAccess()
            // Try exact path first, then with/without trailing slash for directories
            let resolvedPath = path
            if (!this.files.has(path)) {
                const alt = path.endsWith('/') ? path.slice(0, -1) : path + '/'
                if (this.files.has(alt)) resolvedPath = alt
            }
            const existing = this.files.get(resolvedPath)
            if (!existing) {
                // Lazy/on-demand mode: file might exist in the Merkle tree but not be
                // materialized in this.files yet. Enqueue a delete mutation anyway —
                // commitAndEmit's incremental path creates a { type: 'delete' } mutation
                // for paths absent from this.files, and mutateEntry removes the entry
                // from the tree if it exists (or no-ops if it doesn't).
                if (this.currentRootDirHash && this.dirTreeCache?.isLoaded) {
                    // Also evict any stale pending text cache for this path
                    this.pendingTextCache.delete(resolvedPath)
                    this.enqueueMutation(() => this.commitAndEmit([resolvedPath]))
                    return true
                }
                return false
            }

            const pathsToCommit = [resolvedPath]
            this.files.delete(resolvedPath)

            if (existing.kind === 'directory') {
                const prefix = resolvedPath.endsWith('/') ? resolvedPath : resolvedPath + '/'
                for (const p of [...this.files.keys()]) {
                    if (p.startsWith(prefix)) {
                        this.files.delete(p)
                        // For backend efficiency, deleting the parent dir tree node
                        // implicitly deletes all children. No need to bloat pathsToCommit.
                    }
                }
            }

            this.enqueueMutation(() => this.commitAndEmit(pathsToCommit))
            return true
        },
        rename: (oldPath: string, newPath: string): boolean => {
            this.ensureWriteAccess()
            // Try exact path first, then with/without trailing slash for directories
            let resolvedOld = oldPath
            if (!this.files.has(oldPath)) {
                const alt = oldPath.endsWith('/') ? oldPath.slice(0, -1) : oldPath + '/'
                if (this.files.has(alt)) resolvedOld = alt
            }
            const existing = this.files.get(resolvedOld)
            if (!existing) return false
            // Preserve trailing slash convention for directories
            const resolvedNew = existing.kind === 'directory' && !newPath.endsWith('/') ? newPath + '/' : newPath
            
            const pathsToCommit = [resolvedOld, resolvedNew]
            this.files.delete(resolvedOld)
            this.files.set(resolvedNew, { ...existing, path: resolvedNew, mtime: Date.now() })

            if (existing.kind === 'directory') {
                const prefix = resolvedOld.endsWith('/') ? resolvedOld : resolvedOld + '/'
                const newPrefix = resolvedNew.endsWith('/') ? resolvedNew : resolvedNew + '/'
                for (const p of [...this.files.keys()]) {
                    if (p.startsWith(prefix)) {
                        const child = this.files.get(p)!
                        const newChildPath = newPrefix + p.slice(prefix.length)
                        this.files.delete(p)
                        // Don't touch mtime for children during parent move
                        this.files.set(newChildPath, { ...child, path: newChildPath })
                        pathsToCommit.push(newChildPath)
                    }
                }
            }

            this.enqueueMutation(() => this.commitAndEmit(pathsToCommit))
            return true
        },
        mkdir: (path: string): void => {
            this.ensureWriteAccess()
            const dirPath = path.endsWith('/') ? path : path + '/'
            
            const existing = this.files.get(dirPath) || this.files.get(path)
            if (existing) {
                if (existing.kind !== 'directory') throw new Error('ENOTDIR: Not a directory')
                return // already exists as directory
            }

            const now = Date.now()
            this.files.set(dirPath, {
                path: dirPath,
                kind: 'directory',
                mime: '',
                size: 0,
                mtime: now,
                ctime: now,
                content: { type: 'inline', hash: '' },  // placeholder, not used for dirs
            })
            this.events.emit('fs:changed', { paths: [dirPath] })
            this.enqueueMutation(() => this.commitAndEmit([dirPath]))
        },
        symlink: (path: string, target: string): void => {
            this.ensureWriteAccess()
            const now = Date.now()
            const existing = this.files.get(path)
            if (existing?.kind === 'directory') throw new Error('EISDIR: Is a directory')

            this.files.set(path, {
                path,
                kind: 'symlink',
                mime: '',
                size: 0,
                mtime: now,
                ctime: existing?.ctime ?? now,
                target,
                content: { type: 'inline', hash: '' },  // not used for symlinks — target is in dir entry
            })
            this.events.emit('fs:changed', { paths: [path] })
            this.enqueueMutation(() => this.commitAndEmit([path]))
        },
        setMeta: (path: string, meta: Record<string, unknown>): void => {
            this.ensureWriteAccess()
            const existing = this.files.get(path)
            if (!existing) throw new Error(`setMeta: file not found: ${path}`)
            // Update metadata without touching content — v3 only re-encrypts the dir entry
            this.files.set(path, { ...existing, meta, mtime: Date.now() })
            this.events.emit('fs:changed', { paths: [path] })
            this.enqueueMutation(() => this.commitAndEmit([path]))
        },
        importFiles: (
            files: ImportFileEntry[],
            opts?: ImportFilesOptions,
        ): Promise<ImportFilesResult> => {
            this.ensureWriteAccess()
            if (files.length === 0) {
                return Promise.resolve({ created: 0, replaced: 0, paths: [] })
            }
            // Wrap in mutation queue to serialize with concurrent writes
            return new Promise<ImportFilesResult>((resolve, reject) => {
                this.enqueueMutation(async () => {
                    try {
                        const result = await this._importFilesInner(files, opts)
                        resolve(result)
                    } catch (err) {
                        reject(err)
                    }
                })
            })
        },
        listDirAsync: async (path: string): Promise<FileStat[]> => {
            await this.ensureDemandReadableRemoteState()
            const cached = await this.listDirFromCache(path)
            if (cached) return cached

            const prefix = path === '/' ? '/' : path.replace(/\/+$/, '') + '/'
            const result: FileStat[] = []
            // Track synthesized dir metadata: newest mtime + oldest ctime
            const dirMeta = new Map<string, { mtime: number; ctime: number }>()

            for (const file of this.files.values()) {
                if (!file.path.startsWith(prefix)) continue
                const relative = file.path.slice(prefix.length)
                if (relative.length === 0) continue

                const slashIdx = relative.indexOf('/')
                if (slashIdx === -1) {
                    // Direct child file (or explicit directory entry without trailing /)
                    result.push({ ...file })
                } else {
                    // Nested file → track for synthesized directory entry
                    const dirName = relative.slice(0, slashIdx)
                    const dirPath = prefix + dirName + '/'
                    const existing = dirMeta.get(dirPath)
                    if (existing) {
                        existing.mtime = Math.max(existing.mtime, file.mtime)
                        existing.ctime = Math.min(existing.ctime, file.ctime)
                    } else {
                        dirMeta.set(dirPath, { mtime: file.mtime, ctime: file.ctime })
                    }
                }
            }

            // Add synthesized directory entries
            for (const [dirPath, meta] of dirMeta) {
                // Check if explicit directory entry exists in the files Map
                const explicit = this.files.get(dirPath)
                if (explicit) {
                    result.push({ ...explicit })
                } else {
                    result.push({
                        path: dirPath,
                        kind: 'directory',
                        mime: '',
                        size: 0,
                        mtime: meta.mtime,
                        ctime: meta.ctime,
                    })
                }
            }

            return result.sort((a, b) => a.path < b.path ? -1 : a.path > b.path ? 1 : 0)
        },
        listDirStream: async function* (this: RealWorkspace, path: string): AsyncGenerator<FileStat> {
            // For streaming, we still need to collect dir metadata in a first pass,
            // but we yield entries incrementally without building a sorted array.
            // Trade-off: one pass for dirs, then yield files + dirs sorted.
            // For truly O(k) streaming without sort, callers can sort themselves.
            const entries = await this.fs.listDirAsync(path)
            for (const entry of entries) {
                yield entry
            }
        }.bind(this),
    }

    private resetDirTreeCache(rootDirHash: string | null): void {
        this.dirTreeCache = new DirTreeCache(rootDirHash, this.createDirObjectStore(), this.dirTreeDeps())
        this.dirTreeCache.loadLazy()
    }

    private async listDirFromCache(path: string): Promise<FileStat[] | null> {
        if (!this.dirTreeCache) return null
        const dirPath = normalizeDirPath(path)
        const entries = await this.dirTreeCache.readdirAsync(dirPath)
        if (!entries) return null

        const result = entries.map(entry => {
            const entryPath = joinDirEntryPath(dirPath, entry)
            const file = mutableFromDirEntry(entryPath, entry)
            this.files.set(file.path, { ...file })
            this.recordReadMaterializedBaseline(file)
            return { ...file }
        })

        return result.sort((a, b) => a.path.localeCompare(b.path))
    }

    private async materializeFileEntry(path: string): Promise<MutableFileEntry | null> {
        const existing = this.files.get(path)
        if (existing) return existing
        if (!this.dirTreeCache) return null

        const lookupPath = normalizeDirPath(path)
        const entry = await this.dirTreeCache.statAsync(lookupPath)
        if (!entry) return null
        const file = mutableFromDirEntry(joinDirEntryPath(parentDirPath(lookupPath), entry), entry)
        this.files.set(file.path, { ...file })
        this.recordReadMaterializedBaseline(file)
        return file
    }

    private recordReadMaterializedBaseline(file: MutableFileEntry): void {
        if (!this.localHead || this.pendingTextCache.size > 0 || this.queuedMutationCount > 0) return
        const snapshot = this.commitSnapshots.get(this.localHead)
        if (!snapshot || snapshot.has(file.path)) return
        snapshot.set(file.path, {
            path: file.path,
            kind: file.kind,
            mime: file.mime,
            size: file.size,
            mtime: file.mtime,
            ctime: file.ctime ?? file.mtime,
            lastWriterKid: file.lastWriterKid,
            target: file.target,
            content: file.content,
            meta: file.meta,
        })
    }

    createSqliteEngineFactory(
        context: WorkspaceSqliteEngineFactoryContext
    ): Promise<SqliteEngineFactory> | SqliteEngineFactory {
        // Explicit provider takes priority (e.g. test overrides)
        const provider = this.deps.createSqliteEngineFactory
        if (provider) return provider(context)

        // §96 sqlite3mt is the sole built-in engine path. If an old raw `false`
        // value arrives from untyped JS, ignore it and keep the sqlite3mt default.
        const opts = this.useSqlite3Mt ?? {}
        const writerSeed = this.pipeline.getWriterSeed()
        const useMemory = opts.persistence === 'memory'
            || typeof indexedDB === 'undefined'

        const sqlite3mtProvider = createSqlite3MtWorkspaceProvider({
            repository: this.deps.repository,
            crypto: this.crypto,
            stateKey: this.stateKey,
            blobId: this.blobId,
            observers: this.deps.observers,
            logging: this.deps.logging,
            getSyncPolicy: () => this.syncPolicy,
            createDurableLocalStore: ({ headTransport, workspace }) => {
                if (useMemory) {
                    return new InMemoryDurableLocalStore()
                }

                const durableStoreName = buildSqlite3MtDurableIdbName(headTransport.headBlobId)
                sdkConsole.trace('[sqlite3mt:workspace] durable-store', {
                    workspace,
                    headBlobId: headTransport.headBlobId,
                    durableStoreName,
                })
                return new IdbDurableLocalStore(durableStoreName)
            },
            loadSqlite3: initSqlite3 as () => Promise<Sqlite3WasmRuntimeApi>,
            pageSize: opts.pageSize,
            initialWalSize: opts.initialWalSize,
            maxWalSize: opts.maxWalSize,
            faultReadAheadPages: opts.faultReadAheadPages,
            // Node public connections run on worker_threads. A concrete
            // runtime-owner on the main thread would post async page faults to
            // the same thread and then block it in the VFS Atomics.wait loop.
            virtualRuntimeOwner: shouldUseVirtualSqlite3MtRuntimeOwner(),
            resolveHeadTransport: (workspace: string) => ({
                headBlobId: buildBrowserSqliteHeadBlobId(this.blobId, workspace),
                headLane: buildBrowserSqliteHeadLane(this.blobId, workspace),
                writerSeed,
            }),
            onSyncEngineReady: (headBlobId, triggerSync, options) =>
                this.registerSqlite3MtSyncCallback(headBlobId, triggerSync, options),
        })
        return sqlite3mtProvider(context)
    }

    // ── §94 §7: Lease helpers ──────────────────────────────────────

    /** Derive opaque resourceId for lease: HMAC(leaseKey, filePath) — E2E privacy preserved */
    private async computeLeaseResourceId(dbPath: string): Promise<string> {
        const salt = new TextEncoder().encode(`secureblob:${this.blobId}`)
        const info = new TextEncoder().encode('lease-resource-id')
        const readKeyStr = getReadKey(this.deps.entry.capability) ?? this.deps.entry.capability
        const readKeyBytes = base64UrlDecodeBytes(readKeyStr)
        const leaseKey = nobleHkdf(nobleSha256, readKeyBytes, salt, info, 32)
        const pathBytes = new TextEncoder().encode(dbPath)
        const mac = await this.crypto.hmac(leaseKey, pathBytes)
        // Hex-encode first 16 bytes (128-bit, sufficient for uniqueness)
        return Array.from(mac.subarray(0, 16), b => b.toString(16).padStart(2, '0')).join('')
    }

    private ensureOpen(): void {
        if (this.closed || this.tombstoned) {
            throw new Error('WORKSPACE_CLOSED')
        }
    }

    async acquireResourceLease(
        resourceName: string,
        options: { mode?: 0 | 1 | 2; holderId: string; ttlSeconds?: number },
    ): Promise<WorkspaceResourceLease | null> {
        this.ensureOpen()
        if (!this.canWrite) throw new Error('WRITE_FORBIDDEN')
        const resourceId = await this.computeLeaseResourceId(resourceName)
        const lease = await this.deps.repository.acquireLease(
            this.blobId,
            resourceId,
            options.mode ?? 1,
            options.holderId,
            options.ttlSeconds,
        )
        if (!lease) return null
        return {
            leaseId: lease.leaseId,
            resourceId: lease.resourceId || resourceId,
            mode: lease.mode as 0 | 1 | 2,
            expiresAt: lease.expiresAt,
        }
    }

    async refreshResourceLease(leaseId: string, ttlSeconds?: number): Promise<WorkspaceResourceLease | null> {
        this.ensureOpen()
        const lease = await this.deps.repository.refreshLease(this.blobId, leaseId, ttlSeconds)
        if (!lease) return null
        return {
            leaseId: lease.leaseId,
            resourceId: lease.resourceId,
            mode: lease.mode as 0 | 1 | 2,
            expiresAt: lease.expiresAt,
        }
    }

    async releaseResourceLease(leaseId: string): Promise<boolean> {
        this.ensureOpen()
        return this.deps.repository.releaseLease(this.blobId, leaseId)
    }

    /** Get highest escalation mode across all pending .db files */
    private getEffectiveSqliteMode(): { mode: TransactionMode; dbPath: string } | null {
        if (this.pendingSqliteModes.size === 0) return null
        let minMode: TransactionMode = 2
        let minPath = ''
        for (const [path, mode] of this.pendingSqliteModes) {
            if (mode < minMode) { minMode = mode; minPath = path }
        }
        return { mode: minMode, dbPath: minPath }
    }

    /** §94 §7: CAS HEAD update — uses lease if active, plain updateHead otherwise */
    private async casHeadUpdate(
        base: string[],
        newHead: string,
        options?: { referencedHead?: string; releaseLease?: boolean },
    ): Promise<import('./types').HeadUpdateResult> {
        const referencedHead = options?.referencedHead ?? newHead
        const refs = await this.buildReferencedObjectsForHead(referencedHead)
        const hints = this.buildPrefetchHintsForHead(referencedHead)
        const publishPrerequisites = await this.buildHeadPublishPrerequisitesForHead(newHead)
        // Publish visibility and GC liveness are separate contracts.
        //
        // The /heads endpoint must be able to read the new commit object. Readers
        // also need the state object immediately after the head is accepted. Newly
        // produced content/tree objects are already covered by the outbox push
        // barrier before CAS, and OBJECT_MISSING keeps the local graph repair path
        // for old/offline parents that server GC may have removed. The refs list
        // below is metadata for server-side GC; using it as a client-side
        // visibility barrier turns every small publish into a full-workspace
        // object probe.
        await this.ensurePushedObjectsVisible(publishPrerequisites)
        if (this.activeLease) {
            return this.deps.repository.updateHeadWithLease(this.blobId, base, newHead, {
                leaseId: this.activeLease.leaseId,
                // Replay can publish multiple commits in sequence; only release on the last CAS.
                releaseLeaseId: options?.releaseLease === false ? undefined : this.activeLease.leaseId,
                referencedObjects: refs,
                prefetchHints: hints,
            })
        }
        return this.deps.repository.updateHead(this.blobId, base, newHead, refs, hints)
    }

    private async publishResolvedHead(
        baseHead: string | null,
        targetHead: string,
        resolution: SyncResolution,
    ): Promise<{ result: import('./types').HeadUpdateResult; publishedHead: string | null }> {
        if (resolution.kind !== 'replay') {
            return {
                result: await this.casHeadUpdate(baseHead ? [baseHead] : [], targetHead),
                publishedHead: targetHead,
            }
        }

        const replayCommits = resolution.replay.commits
        if (replayCommits.length === 0) {
            return { result: { ok: true }, publishedHead: baseHead }
        }

        let currentBase = resolution.replay.baseHead
        for (let i = 0; i < replayCommits.length; i++) {
            const commitHash = replayCommits[i]
            const result = await this.casHeadUpdate(
                currentBase ? [currentBase] : [],
                commitHash,
                {
                    referencedHead: commitHash,
                    releaseLease: i === replayCommits.length - 1,
                },
            )
            if (!result.ok) {
                return { result, publishedHead: currentBase }
            }
            currentBase = commitHash
        }

        return { result: { ok: true }, publishedHead: currentBase }
    }

    private async ensurePushedObjectsVisible(hashes: string[]): Promise<void> {
        if (hashes.length === 0) return
        await this.deps.repository.ensureRemoteVisible(hashes)
    }

    private async buildHeadPublishPrerequisitesForHead(head: string): Promise<string[]> {
        const hashes = new Set<string>()
        addReferencedObjectHash(hashes, head)
        try {
            const commitBytes = await this.readObject(head)
            if (commitBytes) {
                const commit = JSON.parse(new TextDecoder().decode(commitBytes))
                addReferencedObjectHash(hashes, commit.payload?.stateHash || commit.stateHash)
            }
        } catch {
            // The commit itself remains the minimum publish prerequisite. CAS will
            // surface malformed or missing commit data with its normal error path.
        }
        return Array.from(hashes)
    }

    /** §95 §7.5: Start lease heartbeat (TTL/2 interval) */
    private startLeaseHeartbeat(): void {
        this.stopLeaseHeartbeat()  // clear any existing
        const HEARTBEAT_MS = 15_000  // TTL/2 for 30s default TTL
        this.leaseHeartbeatTimer = setInterval(async () => {
            if (!this.activeLease) {
                this.stopLeaseHeartbeat()
                return
            }
            try {
                const result = await this.deps.repository.refreshLease(
                    this.blobId, this.activeLease.leaseId,
                )
                if (!result) {
                    // Lease expired/revoked by server → degrade to plain CAS
                    this.pushSyncLog({ type: 'sync-start', detail: `Lease ${this.activeLease.leaseId.slice(0, 8)}... expired — degrading to Mode 2` })
                    this.activeLease = null
                    this.stopLeaseHeartbeat()
                }
            } catch {
                // Network error — don't clear lease prematurely. Retry next interval.
                // Lease may still be valid server-side. If truly expired, CAS will
                // still work (just without atomic release).
            }
        }, HEARTBEAT_MS)
    }

    /** §95 §7.5: Stop lease heartbeat */
    private stopLeaseHeartbeat(): void {
        if (this.leaseHeartbeatTimer) {
            clearInterval(this.leaseHeartbeatTimer)
            this.leaseHeartbeatTimer = null
        }
    }

    /** Enrich a CommitSummary with writerName/writerEmoji from knownWriters */
    private enrichCommit(item: CommitSummary): CommitSummary {
        if (item.writerName) return { ...item, affectedFiles: [...item.affectedFiles] }
        const w = item.writerKid ? this.knownWriters.get(item.writerKid) : undefined
        return {
            ...item,
            writerName: w?.name,
            writerEmoji: w?.emoji,
            affectedFiles: [...item.affectedFiles],
        }
    }

    history: History = {
        list: (opts?: { limit?: number; cursor?: string }) => {
            const limit = opts?.limit ?? this.historyIndex.length
            return this.historyIndex.slice(0, limit).map(item => this.enrichCommit(item))
        },
        listForFile: (path: string, opts?: { limit?: number; cursor?: string }) => {
            const limit = opts?.limit ?? this.historyIndex.length
            return this.historyIndex
                .filter(item => item.affectedFiles.some(file => file.path === path))
                .slice(0, limit)
                .map(item => this.enrichCommit(item))
        },
        getCommit: async (hash: string) => {
            const commit = this.historyIndex.find(item => item.commitHash === hash)
            if (!commit) throw new Error(`Commit not found: ${hash}`)
            return this.enrichCommit(commit)
        },
        blame: async (path: string) => {
            return this.computeBlame(path)
        },
        listDirAtCommit: async (path: string, commitHash: string) => this.listDirAtCommit(path, commitHash),
        readTextAtCommit: async (path: string, commitHash: string) => this.readTextAtCommit(path, commitHash),
        readBytesAtCommit: async (path: string, commitHash: string) => this.readBytesAtCommit(path, commitHash),
        diffText: async (path: string, fromCommit: string, toCommit: string) => {
            const fromText = (await this.readTextAtCommit(path, fromCommit)) ?? ''
            const toText = (await this.readTextAtCommit(path, toCommit)) ?? ''
            const fromLines = fromText.split('\n')
            const toLines = toText.split('\n')
            const mapping = computeLineMapping(fromLines, toLines)
            const mappedOld = new Set<number>()
            const hunks: DiffHunk[] = []

            for (const point of mapping) {
                if (point.oldIndex === null) {
                    hunks.push({ type: 'addition', lineNew: point.newIndex + 1, text: toLines[point.newIndex] ?? '' })
                    continue
                }
                mappedOld.add(point.oldIndex)
                hunks.push({
                    type: 'context',
                    lineOld: point.oldIndex + 1,
                    lineNew: point.newIndex + 1,
                    text: toLines[point.newIndex] ?? '',
                })
            }

            for (let i = 0; i < fromLines.length; i++) {
                if (!mappedOld.has(i)) {
                    hunks.push({ type: 'deletion', lineOld: i + 1, text: fromLines[i] ?? '' })
                }
            }

            return hunks
        },
    }

    sharing: Sharing = {
        createShareLink: (mode, path, viewMode) => {
            const appBase = typeof window !== 'undefined' ? window.location.origin + window.location.pathname : ''
            const modeCapability = mode === 'read' ? redactCapability(this.deps.entry.capability) : this.deps.entry.capability
            const hashParams = new URLSearchParams({
                k: modeCapability,
                ...(path ? { path: path.replace(/^\/+/, '') } : {}),
                ...(this.label ? { title: this.label } : {}),
                ...(this.emoji ? { emoji: this.emoji } : {}),
            })
            if (mode === 'readwrite') hashParams.set('w', '1')
            if (viewMode && path) hashParams.set('view', viewMode)
            const url = `${appBase}#${encodeURIComponent(this.blobId)}&${hashParams.toString()}`
            return { url, mode }
        },
        // Async invite creation via server control-plane (Spec §6.1 POST /invites, §13).
        createInviteLink: async (mode, path, viewMode) => {
            const authProvider = this.deps.authProvider
            if (!authProvider) {
                throw new Error('Cannot create invite: no auth provider available (workspace may not support v2 auth).')
            }

            const secret = decodeWorkspaceSecretV2(this.deps.entry.capability)
            if (!secret) {
                throw new Error('Cannot create invite: workspace entry does not contain a valid workspace secret v2.')
            }

            // Need a write-scoped token to call POST /invites (Spec §6.1)
            const writeTokenObj = await authProvider.getAccessToken('write')
            const inviteMode = mode === 'readwrite' ? 'write' as const : 'read' as const

            const result = await createWorkspaceInviteV2({
                apiBaseUrl: this.deps.entry.apiBaseUrl,
                inviterSecret: secret,
                writeToken: writeTokenObj.token,
                mode: inviteMode,
                title: this.label || undefined,
                emoji: this.emoji || undefined,
                path: path?.replace(/^\/+/, '') || undefined,
                view: viewMode || undefined,
                app: 'webapp',
            })

            const appBase = typeof window !== 'undefined' ? window.location.origin + window.location.pathname : ''
            const url = `${appBase}#invite=${result.encodedInvite}`

            return {
                url,
                mode,
            }
        },
    }

    // §17 Presence — heuristic: self + writers with recent file activity
    presence: PresenceManager = {
        startHeartbeat: () => {
            if (this.heartbeatTimer) return
            // Emit initial state immediately
            this.presenceEvents.emit('presence:changed', this.buildPresenceList())
            // Then emit every 30s (matches spec §17.2)
            this.heartbeatTimer = setInterval(() => {
                this.presenceEvents.emit('presence:changed', this.buildPresenceList())
            }, 30_000)
        },
        stopHeartbeat: () => {
            if (this.heartbeatTimer) {
                clearInterval(this.heartbeatTimer)
                this.heartbeatTimer = null
            }
        },
        getViewers: () => this.buildPresenceList(),
        on: (event: 'presence:changed', fn: (viewers: PresenceEntry[]) => void) =>
            this.presenceEvents.on(event, fn),
    }

    /** Update the active file tracked for "self" presence entry. */
    setActiveFile(path: string | undefined): void {
        this.currentActiveFile = path
    }

    // ── Writer management ─────────────────────────────────────────

    getWriters(): WriterInfo[] {
        return [...this.knownWriters.values()]
    }

    async removeWriter(kid: string): Promise<void> {
        this.ensureWriteAccess()
        if (kid === this.derivedWriterKid) {
            throw new Error('Cannot remove yourself as writer')
        }
        // Remove from known writers
        this.knownWriters.delete(kid)
        // Remove from persisted writer metadata
        this.writerStateEntries = this.writerStateEntries.filter(w => w.kid !== kid)
        // Commit updated state (writers[] in state will be rebuilt by commitAndEmit)
        await this.commitAndEmitWithWriters()
    }

    resolveWriterName(kid: string): { name?: string; emoji?: string } {
        const w = this.knownWriters.get(kid)
        return w ? { name: w.name, emoji: w.emoji } : {}
    }

    // ── Writer helpers ─────────────────────────────────────────────

    /** Ensure the self writer is always tracked. */
    private ensureSelfWriter(): void {
        const selfKid = this.derivedWriterKid
        if (selfKid === 'anonymous') return // Read-only

        // §3.1.1: Resolve self name/emoji from identity vault
        const identity = this.deps.identityProvider?.getIdentity()
        const selfName = identity?.name
        const selfEmoji = identity?.emoji

        const existing = this.knownWriters.get(selfKid)
        if (!existing) {
            // Derive pk from writer seed for self entry
            const writerSeed = (this.pipeline as any).deps.writerSeed as Uint8Array | undefined
            const pkBase64 = writerSeed
                ? this.deriveSelfPkSync(writerSeed)
                : ''

            this.knownWriters.set(selfKid, {
                kid: selfKid,
                pk: pkBase64,
                name: selfName,
                emoji: selfEmoji,
                role: this.isFirstWriter(selfKid) ? 'owner' : 'editor',
            })
        } else {
            // Always keep self name/emoji fresh from identity vault
            if (selfName) existing.name = selfName
            if (selfEmoji) existing.emoji = selfEmoji
        }
    }

    /** Derive pk as base64url from writer seed (sync — cached in pipeline). */
    private deriveSelfPkSync(_seed: Uint8Array): string {
        // pk derivation is async (ed25519), but we can use a placeholder and
        // update it asynchronously. The parseCommitObject flow will fill it.
        return ''
    }

    /** Check if a kid is the first writer (owner) by checking commit order. */
    private isFirstWriter(kid: string): boolean {
        // The first writer in the knownWriters map is the owner
        if (this.knownWriters.size === 0) return true
        const first = this.knownWriters.values().next().value
        return first?.kid === kid
    }

    /**
     * Extract writers from a parsed commit record.
     * CommitRecord.writers[] has {alg, kid, pk} per writer.
     */
    private extractWritersFromCommit(commit: any): void {
        const writers = commit.writers as Array<{ alg: string; kid: string; pk: string }> | undefined
        if (!writers?.length) return

        for (const w of writers) {
            const existing = this.knownWriters.get(w.kid)
            // Look up persisted metadata (name, emoji) from state
            const stateEntry = this.writerStateEntries.find(s => s.kid === w.kid)

            if (!existing) {
                // Determine role: first writer seen → owner, others → editor
                const role = this.knownWriters.size === 0 ? 'owner' : 'editor'
                this.knownWriters.set(w.kid, {
                    kid: w.kid,
                    pk: w.pk,
                    name: stateEntry?.name,
                    emoji: stateEntry?.emoji,
                    role,
                })
            } else {
                // Update pk if we now have a better value
                if (w.pk && !existing.pk) {
                    existing.pk = w.pk
                }
                // Update name/emoji from state metadata if available
                if (stateEntry?.name && !existing.name) existing.name = stateEntry.name
                if (stateEntry?.emoji && !existing.emoji) existing.emoji = stateEntry.emoji
            }
        }

        // Also track lastWriterKid from files (adds kids we haven't seen in writers[] yet)
        for (const file of this.files.values()) {
            if (file.lastWriterKid && !this.knownWriters.has(file.lastWriterKid)) {
                this.knownWriters.set(file.lastWriterKid, {
                    kid: file.lastWriterKid,
                    pk: '',
                    role: 'editor',
                })
            }
        }
    }

    /**
     * Build the heuristic presence list.
     * Always includes self. Includes other writers who have recent lastWriterKid activity.
     */
    private buildPresenceList(): PresenceEntry[] {
        const viewers: PresenceEntry[] = []
        const selfKid = this.derivedWriterKid

        // Self is always present
        if (selfKid !== 'anonymous') {
            const selfWriter = this.knownWriters.get(selfKid)
            viewers.push({
                kid: selfKid,
                name: selfWriter?.name,
                emoji: selfWriter?.emoji,
                activeFile: this.currentActiveFile,
                device: 'web',
                isSelf: true,
            })
        }

        // Add other writers who have recent file activity (within last 5 minutes)
        const recentThreshold = Date.now() - 5 * 60 * 1000
        const recentWriterKids = new Set<string>()
        for (const file of this.files.values()) {
            if (file.lastWriterKid && file.lastWriterKid !== selfKid && file.mtime > recentThreshold) {
                recentWriterKids.add(file.lastWriterKid)
            }
        }

        for (const kid of recentWriterKids) {
            const writer = this.knownWriters.get(kid)
            // Find the most recently edited file by this writer
            let activeFile: string | undefined
            let latestMtime = 0
            for (const file of this.files.values()) {
                if (file.lastWriterKid === kid && file.mtime > latestMtime) {
                    latestMtime = file.mtime
                    activeFile = file.path
                }
            }

            viewers.push({
                kid,
                name: writer?.name,
                emoji: writer?.emoji,
                activeFile,
                device: 'web',
                isSelf: false,
            })
        }

        return viewers
    }

    /**
     * Commit with writer metadata in state.
     * Used by removeWriter to persist the updated writers list.
     */
    private async commitAndEmitWithWriters(): Promise<void> {
        // Build writer state entries from knownWriters map
        const writerEntries: WriterStateEntry[] = [...this.knownWriters.values()].map(w => ({
            kid: w.kid,
            pk: w.pk,
            name: w.name,
            emoji: w.emoji,
            addedAt: this.writerStateEntries.find(e => e.kid === w.kid)?.addedAt ?? Date.now(),
        }))
        this.writerStateEntries = writerEntries
        // §7.2.3: No file mutations — only writer metadata in state changes
        await this.commitAndEmit([])
    }

    on<E extends WorkspaceEventType>(event: E, fn: (payload: WorkspaceEventPayload[E]) => void): () => void { return this.events.on(event, fn) }
    off<E extends WorkspaceEventType>(event: E, fn: (payload: WorkspaceEventPayload[E]) => void): void { this.events.off(event, fn) }

    async syncNow(): Promise<void> {
        await this.syncNowInternal({ forceEagerSnapshot: true })
    }

    async syncOnDemand(): Promise<void> {
        await this.syncNowInternal({ preferLazyRemoteAdoption: true })
    }

    private async syncNowInternal(options: { preferLazyRemoteAdoption?: boolean; forceEagerSnapshot?: boolean } = {}): Promise<void> {
        await this.waitForSyncReadyBarrier()

        // E3: Check ALL conditions including syncPaused and importing (covers all trigger paths)
        if (this.tombstoned || this.closed || this.syncPaused) return
        if (this.syncing || this.committing || this.importing) {
            this.syncRequestedWhileBusy = true
            return
        }

        // Short-circuit when offline — skip all network calls
        if (!resolveNavigatorOnlineForWorkspaceSync()) {
            this.refreshState()
            this.events.emit('state:changed', this.state)
            return
        }

        this.syncing = true
        this.syncAbortController = new AbortController()
        const syncStartTime = Date.now()
        this.pushSyncLog({ type: 'sync-start', detail: 'Sync started' })
        this.syncProgressData = undefined
        this.refreshState()
        this.events.emit('state:changed', this.state)
        let prePushResolution: SyncResolution = { kind: 'none', touchedConflictState: false }
        try {
            // ── Step 1: Check remote BEFORE push ─────────────────────
            // Discovering divergence here (50ms) instead of after a 13-min push
            // eliminates the re-upload loop entirely.
            const { heads, prefetchHints } = await this.getRemoteHeadsWithBootstrapRecovery()
            let remoteHead = heads[0] || null
            const hadKnownRemoteHeadBeforeSync = this.remoteHead != null
            let shouldRebootstrapLocalGraph = false
            sdkConsole.debug(`[Sync] syncNow blob=${this.blobId.slice(0, 12)} localHead=${this.localHead?.slice(0, 8) ?? 'none'} remoteHead=${remoteHead?.slice(0, 8) ?? 'none'}`)

            this.prefetchRemoteHintsIfEager(prefetchHints)

            // ── Step 2: Resolve divergence BEFORE push ───────────────
            // If remote head differs from local, merge/fast-forward/adopt FIRST.
            // This way the CAS after push should succeed on the first try.
            const localHead = this.localHead
            if (!localHead && remoteHead) {
                // No local state — pull remote
                sdkConsole.debug(`[Sync] no local head, pulling remote ${remoteHead.slice(0, 8)}`)
                await this.pullRemoteStateIfNeeded(options.preferLazyRemoteAdoption === true)
                this.lastSyncAt = Date.now()
                this.pendingChanges = 0
                this.lastSyncError = undefined
                this.lastConflictPaths = []
                this.pushSyncLog({ type: 'sync-ok', durationMs: Date.now() - syncStartTime, detail: 'Pulled remote state (no local head)' })
                return // nothing to push
            }

            // §7.2.3 bugfix: Local head exists but remote head is absent (e.g., owner
            // rejoins after server-side head was cleared). Pull remote first to adopt
            // any server state. If the remote is genuinely empty after pulling, this is
            // an initial-push scenario — fall through to push the local commit instead
            // of silently dropping it. Only exit when pulling actually adopted remote state.
            if (localHead && !remoteHead) {
                sdkConsole.debug(`[Sync] local head ${localHead.slice(0, 8)} present but no remote head — pulling remote`)
                const prePullLocalHead = this.localHead
                await this.pullRemoteStateIfNeeded(options.preferLazyRemoteAdoption === true)
                // After pull: if remote was genuinely empty (no head adopted), this is
                // an initial push — fall through to push phase. Otherwise we adopted
                // remote state (owner-rejoin), so exit.
                if (this.localHead !== prePullLocalHead) {
                    // Pull adopted remote state — localHead changed
                    this.lastSyncAt = Date.now()
                    this.pendingChanges = 0
                    this.lastSyncError = undefined
                    this.lastConflictPaths = []
                    this.pushSyncLog({ type: 'sync-ok', durationMs: Date.now() - syncStartTime, detail: 'Pulled remote state (owner rejoin)' })
                    return
                }
                // Remote was empty — this is an initial push. Fall through.
                shouldRebootstrapLocalGraph = hadKnownRemoteHeadBeforeSync
                sdkConsole.debug(`[Sync] remote empty after pull — continuing to push local head`)
            }

            if (localHead && localHead === remoteHead) {
                if (options.forceEagerSnapshot) {
                    await this.ensureExplicitSyncSnapshot(localHead)
                } else {
                    this.syncEnsureHistory(localHead)
                }
                // Still continue — outbox may have objects from previous failed sync
            }

            if (localHead && remoteHead && localHead !== remoteHead) {
                if (!this.canWrite) {
                    await this.pullRemoteStateIfNeeded(options.preferLazyRemoteAdoption === true)
                    this.lastSyncAt = Date.now()
                    this.pendingChanges = 0
                    this.lastSyncError = undefined
                    this.lastConflictPaths = []
                    this.pushSyncLog({ type: 'sync-ok', durationMs: Date.now() - syncStartTime, detail: 'Pulled remote state (read-only)' })
                    return
                }
                // Pre-push merge: resolves CAS_MISMATCH locally before uploading anything
                prePushResolution = await this.syncResolveDivergence(remoteHead, {
                    preferLazyRemoteAdoption: options.preferLazyRemoteAdoption === true,
                })
                // After merge: this.localHead is the merge commit, remoteHead is updated
                remoteHead = this.remoteHead ?? remoteHead
            }

            // ── Step 2.5: Acquire resource lease for Mode 1/0 (§95 §7) ──
            // Per §90 §3.3: acquire BEFORE push to serialize writers during upload
            const pending = this.getEffectiveSqliteMode()
            this.pendingSqliteModes.clear()  // consume

            if (pending && pending.mode < 2) {
                try {
                    const resourceId = await this.computeLeaseResourceId(pending.dbPath)
                    const lease = await this.deps.repository.acquireLease(
                        this.blobId, resourceId, pending.mode, this.derivedWriterKid,
                    )
                    if (lease) {
                        this.activeLease = {
                            leaseId: lease.leaseId,
                            resourceId,
                            mode: lease.mode as 0 | 1 | 2,
                        }
                        // §95 §7.5: Start heartbeat (TTL/2 interval) to keep lease alive during push
                        this.startLeaseHeartbeat()
                        this.pushSyncLog({ type: 'sync-start', detail: `Acquired Mode ${pending.mode} lease on ${pending.dbPath}: ${lease.leaseId.slice(0, 8)}...` })
                    }
                    // null → graceful degradation to Mode 2 (plain CAS)
                } catch {
                    // Lease server unreachable → degrade to Mode 2
                    this.pushSyncLog({ type: 'sync-start', detail: `Lease acquire failed for ${pending.dbPath} — degrading to Mode 2` })
                }
            }

            // ── Step 3: Push with abort signal + progress reporting ──
            // §7.8.4: Upload outbox objects. After pre-push merge, the CAS
            // base is already correct so the push won't be wasted.
            const pushedHashes = await this.deps.repository.push(this.blobId, {
                signal: this.syncAbortController.signal,
                onProgress: (uploaded, total, bytesUp, bytesTotal) => {
                    this.syncProgressData = {
                        phase: 'push', objectsUploaded: uploaded,
                        objectsTotal: total, bytesUploaded: bytesUp, bytesTotal,
                    }
                    this.refreshState()
                    this.events.emit('state:changed', this.state)
                },
            })

            // E6: If aborted during push, leave the outbox intact and exit.
            // The next sync pass must still run the current publish barrier
            // before clearing outbox entries or attempting HEAD CAS.
            if (this.syncAbortController.signal.aborted) {
                this.pushSyncLog({ type: 'sync-ok', durationMs: Date.now() - syncStartTime, detail: `Sync paused after uploading ${pushedHashes.length} objects` })
                return
            }

            // ── Step 4: Publish barrier + outbox confirmation ─────────
            // Upload ACK is a response-time transport fact, not the authority
            // for whether /heads can read the new commit graph. Probe the
            // changed object set before CAS; then clear the outbox while the
            // local object store remains the recovery source if a later CAS
            // returns OBJECT_MISSING or the browser crashes mid-publish.
            if (pushedHashes.length > 0) {
                await this.ensurePushedObjectsVisible(pushedHashes)
                await this.deps.repository.confirmPush(this.blobId, pushedHashes)
            }
            if (shouldRebootstrapLocalGraph && this.localHead) {
                this.pushSyncLog({
                    type: 'sync-start',
                    detail: 'Remote head is absent but this client has a previously-synced head — re-pushing local object graph before bootstrap CAS',
                })
                await this.repushLocalObjectGraph(this.localHead)
            }

            // ── Step 5: CAS HEAD update ──────────────────────────────
            // Update phase to CAS for UI
            this.syncProgressData = { phase: 'cas', objectsUploaded: pushedHashes.length, objectsTotal: pushedHashes.length, bytesUploaded: 0, bytesTotal: 0 }
            this.refreshState()
            this.events.emit('state:changed', this.state)

            const currentLocalHead = this.localHead
            if (!currentLocalHead || currentLocalHead === remoteHead) {
                // Already synced (adopted remote or no changes)
                this.pendingSqliteModes.clear()
                this.lastSyncAt = Date.now()
                this.pendingChanges = 0
                this.lastSyncError = undefined
                if (!prePushResolution.touchedConflictState) {
                    this.lastConflictPaths = []
                }
                this.pushSyncLog({ type: 'sync-ok', durationMs: Date.now() - syncStartTime, detail: 'Sync completed (already in sync)' })
                return
            }

            const baseForLog = prePushResolution.kind === 'replay'
                ? (prePushResolution.replay.baseHead ? [prePushResolution.replay.baseHead] : [])
                : (remoteHead ? [remoteHead] : [])
            sdkConsole.debug(`[Sync] CAS attempt blob=${this.blobId.slice(0, 12)} base=[${baseForLog.map(h => h.slice(0, 8))}] new=${currentLocalHead.slice(0, 8)}`)
            let publishResult = await this.publishResolvedHead(remoteHead, currentLocalHead, prePushResolution)
            let result = publishResult.result

            if (result.ok) {
                this.activeLease = null  // server released atomically via releaseLeaseId
                await this.updateHeads(undefined, publishResult.publishedHead ?? currentLocalHead)
                this.lastSyncAt = Date.now()
                this.pendingChanges = 0
                this.lastSyncError = undefined
                if (!prePushResolution.touchedConflictState) {
                    this.lastConflictPaths = []
                }
                this.pushSyncLog({ type: 'sync-ok', durationMs: Date.now() - syncStartTime, detail: 'Sync completed' })
                // M1: Proactive GC — fire-and-forget
                if (this.deps.localBackend?.runGC) {
                    const protectedHashes = await this.collectProtectedHashes()
                    void this.deps.localBackend.runGC(10_000, protectedHashes)
                }
                return
            }

            // ── Step 6: Post-push error handling ─────────────────────
            // Objects are already on the server (step 3) and outbox is cleared
            // (step 4). Any recovery here only needs to upload merge objects,
            // NEVER the original content — eliminating the re-upload loop.
            let casMismatchRetries = 0
            while (result.code === 'CAS_MISMATCH') {
                if (this.syncAbortController.signal.aborted || this.tombstoned || this.closed || this.syncPaused) {
                    this.pushSyncLog({ type: 'sync-ok', durationMs: Date.now() - syncStartTime, detail: `Sync paused during CAS contention after ${casMismatchRetries} re-merge${casMismatchRetries === 1 ? '' : 's'}` })
                    return
                }
                casMismatchRetries += 1
                // Remote head changed during push. Re-merge and retry CAS.
                // Only merge objects (commit + state, ~KB) need uploading.
                const newRemoteHead = result.remoteHead || undefined
                sdkConsole.warn(`[Sync] CAS_MISMATCH blob=${this.blobId.slice(0, 12)} serverHead=${newRemoteHead?.slice(0, 8)} — re-merging (attempt ${casMismatchRetries})`)
                this.pushSyncLog({ type: 'sync-start', detail: `CAS mismatch after push — re-merging with ${newRemoteHead?.slice(0, 8)} (attempt ${casMismatchRetries})` })
                prePushResolution = await this.syncResolveDivergence(newRemoteHead, {
                    preferLazyRemoteAdoption: options.preferLazyRemoteAdoption === true,
                })

                // Push only the new merge objects (commit + state, not content)
                const mergeUploaded = await this.deps.repository.push(this.blobId)
                if (mergeUploaded.length > 0) {
                    await this.ensurePushedObjectsVisible(mergeUploaded)
                    await this.deps.repository.confirmPush(this.blobId, mergeUploaded)
                }

                publishResult = await this.publishResolvedHead(newRemoteHead ?? null, this.localHead!, prePushResolution)
                result = publishResult.result

                if (result.ok) {
                    this.activeLease = null  // server released atomically
                    await this.updateHeads(undefined, publishResult.publishedHead ?? this.localHead!)
                    this.lastSyncAt = Date.now()
                    this.pendingChanges = 0
                    this.lastSyncError = undefined
                    if (!prePushResolution.touchedConflictState) {
                        this.lastConflictPaths = []
                    }
                    this.pushSyncLog({ type: 'sync-ok', durationMs: Date.now() - syncStartTime, detail: `Sync completed after ${casMismatchRetries} re-merge${casMismatchRetries === 1 ? '' : 's'}` })
                    if (this.deps.localBackend?.runGC) {
                        const protectedHashes = await this.collectProtectedHashes()
                        void this.deps.localBackend.runGC(10_000, protectedHashes)
                    }
                    return
                }
                // Outbox is ALREADY EMPTY — this loop only pushes merge commits.
            }

            if (result.code === 'OBJECT_MISSING') {
                // Server lost objects. Re-push from local IDB objects store.
                // NOTE: Use this.localHead (not currentLocalHead) because step 6's
                // syncResolveDivergence may have changed localHead to a merge commit.
                const recoveryHead = this.localHead!
                this.pushSyncLog({ type: 'sync-start', detail: 'Server objects missing — re-pushing from local store' })
                await this.repushLocalObjectGraph(recoveryHead)
                const rePushed = await this.deps.repository.push(this.blobId)
                await this.ensurePushedObjectsVisible(rePushed)
                const bootstrapResult = await this.casHeadUpdate([], recoveryHead)
                if (bootstrapResult.ok) {
                    this.activeLease = null  // server released atomically
                    if (rePushed.length > 0) await this.deps.repository.confirmPush(this.blobId, rePushed)
                    await this.updateHeads(undefined, recoveryHead)
                    this.lastSyncAt = Date.now()
                    this.pendingChanges = 0
                    this.lastSyncError = undefined
                    this.lastConflictPaths = []
                    this.pushSyncLog({ type: 'sync-ok', durationMs: Date.now() - syncStartTime, detail: 'Sync completed after object recovery' })
                    return
                }
                // Bootstrap failed — genesis re-commit
                this.pushSyncLog({ type: 'sync-start', detail: 'Bootstrap failed — genesis re-commit' })
                this.localHeadParents = []
                // §7.2.3: Reset root for genesis — full rebuild via O(n) buildTreeFromPaths
                this.currentRootDirHash = null
                this.dirTreeCache = null
                await this.commitAndEmit([...this.files.keys()], undefined, true)
                // New commit will trigger another syncNow via triggerSync
                this.lastSyncAt = Date.now()
                this.pendingChanges = 0
                this.lastSyncError = undefined
                this.lastConflictPaths = []
                this.pushSyncLog({ type: 'sync-ok', durationMs: Date.now() - syncStartTime, detail: 'Genesis re-commit completed' })
                return
            }

            if (result.code === 'COMMIT_INVALID') {
                // §7.2.4: Local commit chain issue — merge and retry
                this.pushSyncLog({ type: 'sync-start', detail: `COMMIT_INVALID: ${result.message}. Merging with remote head and retrying.` })
                const { heads: currentHeads } = await this.getRemoteHeadsWithBootstrapRecovery()
                const currentRemoteHead = currentHeads[0] || remoteHead || undefined
                prePushResolution = await this.syncResolveDivergence(currentRemoteHead, {
                    preferLazyRemoteAdoption: options.preferLazyRemoteAdoption === true,
                })
                const invalidMergeUploaded = await this.deps.repository.push(this.blobId)
                if (invalidMergeUploaded.length > 0) {
                    await this.ensurePushedObjectsVisible(invalidMergeUploaded)
                    await this.deps.repository.confirmPush(this.blobId, invalidMergeUploaded)
                }
                const retryPublish = await this.publishResolvedHead(currentRemoteHead ?? null, this.localHead!, prePushResolution)
                const retryResult = retryPublish.result
                if (retryResult.ok) {
                    this.activeLease = null  // server released atomically
                    await this.updateHeads(undefined, retryPublish.publishedHead ?? this.localHead!)
                    this.lastSyncAt = Date.now()
                    this.pendingChanges = 0
                    this.lastSyncError = undefined
                    if (!prePushResolution.touchedConflictState) {
                        this.lastConflictPaths = []
                    }
                    this.pushSyncLog({ type: 'sync-ok', durationMs: Date.now() - syncStartTime, detail: 'Sync completed after COMMIT_INVALID recovery' })
                    return
                }
                // Fall through to generic error
            }

            // Generic error for unhandled codes
            throw new EngineException({
                code: (result.code as EngineErrorCode) || 'NETWORK_ERROR',
                message: ('message' in result ? result.message : undefined) || 'Sync failed',
                retryable: true,
            })
        } catch (error) {
            let engineError: EngineError
            if (error instanceof EngineException) {
                engineError = error.error
            } else {
                engineError = {
                    code: 'NETWORK_ERROR' as const,
                    message: error instanceof Error ? error.message : 'Sync failed',
                    retryable: true,
                }
            }

            if (engineError.code === 'BLOB_SOFT_DELETED') {
                this.tombstoned = true
                this.lastSyncError = undefined
                this.close()
                return
            }

            this.lastSyncError = engineError
            this.pushSyncLog({ type: 'sync-error', durationMs: Date.now() - syncStartTime, error: engineError, detail: engineError.message })
            throw error
        } finally {
            const runQueuedSync = this.syncRequestedWhileBusy
            this.syncRequestedWhileBusy = false
            // §95 §7.5: Stop heartbeat + release lease
            this.stopLeaseHeartbeat()
            if (this.activeLease) {
                this.deps.repository.releaseLease(this.blobId, this.activeLease.leaseId).catch(() => { /* best-effort */ })
                this.activeLease = null
            }
            this.syncing = false
            // §7.8.3: Preserve progress data when paused so UI shows partial %
            if (!this.syncPaused) {
                this.syncProgressData = undefined
            }
            this.syncAbortController = null
            this.refreshState()
            this.events.emit('state:changed', this.state)
            if (this.syncPolicy === 'auto' && !this.syncPaused && !this.lastSyncError && !this.closed && !this.tombstoned) {
                void this.prefetchContent().catch(err => {
                    sdkConsole.warn('Auto-sync content prefetch failed after sync', err)
                })
            }
            // §7.8.3 Race fix: If sync was aborted (pause) and then resumed
            // while the old sync was still completing, resumeSync's triggerSync()
            // would have no-oped against the syncing guard. Re-trigger here
            // to flush remaining outbox items immediately.
            const flushedPeerHead = await this.flushQueuedPeerLocalCommit()
            if (!this.syncPaused && (this.pendingChanges > 0 || runQueuedSync || flushedPeerHead)) {
                this.triggerSync()
            }
        }
    }

    private async waitForSyncReadyBarrier(): Promise<void> {
        while (!this.tombstoned && !this.closed && !this.syncPaused) {
            await this.waitForQueuedMutations()

            if (!this.syncing && !this.committing && !this.importing) {
                return
            }

            this.syncRequestedWhileBusy = true
            if (this.syncing) {
                await this.waitForActiveSync()
            } else {
                await new Promise(resolve => setTimeout(resolve, 0))
            }
        }
    }

    private async waitForQueuedMutations(): Promise<void> {
        while (true) {
            const queue = this.mutationQueue
            await queue
            if (queue === this.mutationQueue) {
                return
            }
        }
    }

    private async waitForActiveSync(): Promise<void> {
        if (!this.syncing) return

        await new Promise<void>(resolve => {
            const unsubscribe = this.events.on('state:changed', state => {
                if (!state.syncing) {
                    unsubscribe()
                    resolve()
                }
            })

            if (!this.syncing) {
                unsubscribe()
                resolve()
            }
        })
    }

    getSyncPolicy(): WorkspaceSyncPolicy {
        return this.syncPolicy
    }

    setSyncPolicy(policy: WorkspaceSyncPolicy): void {
        const previous = this.syncPolicy
        if (previous === policy) {
            this.applyPollingPolicy()
            this.refreshState()
            this.events.emit('state:changed', this.state)
            return
        }
        this.syncPolicy = policy
        this.syncPaused = policy === 'paused'
        this.applyPollingPolicy()

        if (policy === 'paused') {
            this.syncAbortController?.abort('pause')
        } else if (previous === 'paused') {
            this.syncProgressData = undefined
            this.triggerSync()
            this.drainPendingSqlite3MtSyncs()
            if (policy === 'auto') {
                void this.prefetchContent().catch(err => {
                    sdkConsole.warn('Auto-sync content prefetch failed', err)
                })
            }
        } else if (policy === 'auto') {
            this.triggerSync()
            this.drainPendingSqlite3MtSyncs()
            void this.prefetchContent().catch(err => {
                sdkConsole.warn('Auto-sync content prefetch failed', err)
            })
        }

        this.refreshState()
        this.events.emit('state:changed', this.state)
    }

    async prefetchContent(paths?: string | readonly string[]): Promise<void> {
        await this.ensureDemandReadableRemoteState()
        if (paths == null) {
            await this.materializeFullFileIndex()
        }
        const targetPaths = paths == null
            ? [...this.files.keys()]
            : (typeof paths === 'string' ? [paths] : [...paths])

        for (const path of targetPaths) {
            if (this.closed || this.tombstoned) return
            if (this.syncPolicy === 'paused') return
            const file = await this.materializeFileEntry(path)
            if (!file || file.kind !== 'file') continue
            await this.pipeline.readContent(file.content)
        }
    }

    /** §7.8.3: Pause sync — abort in-flight push, block future syncs */
    pauseSync(): void {
        this.setSyncPolicy('paused')
    }

    /** §7.8.3: Resume sync — unblock and trigger immediate sync */
    resumeSync(): void {
        this.setSyncPolicy('on-demand')
    }

    private drainPendingSqlite3MtSyncs(): void {
        if (this.syncPolicy !== 'auto') return
        const pending = [...this.pendingSqlite3MtSyncHeadIds]
        this.pendingSqlite3MtSyncHeadIds.clear()
        for (const headBlobId of pending) {
            this.sqlite3mtSyncCallbacks.get(headBlobId)?.()
        }
    }

    /** §10.1.3: Ensure history is loaded (restore from sessionStorage or walk chain) */
    private syncEnsureHistory(localHead: string): void {
        if (this.historyIndex.length === 0 && localHead) {
            if (!this.restoreHistoryIndex() || this.historyIndex[0]?.commitHash !== localHead) {
                this.historyIndex = []
                if (this.syncPolicy === 'on-demand') return
                void this.rebuildHistoryIndex(localHead)
            } else {
                this.events.emit('history:changed', { newCommits: this.historyIndex.map(c => c.commitHash) })
            }
        }
    }

    private async ensureExplicitSyncSnapshot(localHead: string): Promise<void> {
        if (this.dirTreeCache?.isLoaded && this.currentRootDirHash) {
            await this.materializeFullFileIndex()
            this.commitSnapshots.set(localHead, this.captureFileSnapshot())
        }
        if (this.historyIndex.length === 0 && localHead) {
            if (!this.restoreHistoryIndex() || this.historyIndex[0]?.commitHash !== localHead) {
                await this.rebuildHistoryIndex(localHead)
            } else {
                this.events.emit('history:changed', { newCommits: this.historyIndex.map(c => c.commitHash) })
            }
        }
    }

    /**
     * Integrate a newer local head from another tab before it reaches the server.
     * Without this, same-device editors can continue from stale state and later
     * lose text when the eventual remote merge falls back to remote_wins.
     */
    private async handlePeerLocalCommit(peerHead: string): Promise<void> {
        if (!peerHead || peerHead === this.localHead || this.tombstoned) return

        // Avoid mutating state while another local commit/import/sync is in flight.
        // The peer head itself must be queued; a later generic sync cannot recover
        // a same-device local-only commit that has not reached the server yet.
        if (this.syncing || this.committing || this.importing || this.queuedMutationCount > 0) {
            this.queuePeerLocalCommit(peerHead)
            return
        }

        if (!this.localHead) {
            await this.adoptPipelineCommit(peerHead, false)
            this.triggerSync()
            return
        }

        const relation = await this.analyzeHeadRelation(this.localHead, peerHead)
        if (relation.kind === 'same' || relation.kind === 'local_ahead') return

        if (relation.kind === 'remote_ahead') {
            const lastSnapshot = this.commitSnapshots.get(this.localHead)
            if (this.hasUncommittedLocalState(lastSnapshot)) {
                this.triggerSync()
                return
            }
            await this.adoptPipelineCommit(peerHead, false)
            this.triggerSync()
            return
        }

        const peerState = await this.fetchStateStrict(peerHead, 'Cross-tab local head')
        await this.mergeStates(
            peerState,
            peerHead,
            this.localHead,
            relation.mergeBase,
            { updateRemoteHead: false },
        )
        this.triggerSync()
    }

    private queuePeerLocalCommit(peerHead: string): void {
        if (!peerHead || peerHead === this.localHead || this.tombstoned) return
        this.queuedPeerLocalHead = peerHead
        this.syncRequestedWhileBusy = true
        this.triggerSync()
    }

    private async flushQueuedPeerLocalCommit(): Promise<boolean> {
        if (!this.queuedPeerLocalHead || this.syncing || this.committing || this.importing || this.queuedMutationCount > 0 || this.closed || this.tombstoned) {
            return false
        }

        const peerHead = this.queuedPeerLocalHead
        this.queuedPeerLocalHead = null
        try {
            await this.handlePeerLocalCommit(peerHead)
            return true
        } catch (error) {
            sdkConsole.warn('Queued cross-tab peer commit handling failed', error)
            this.queuePeerLocalCommit(peerHead)
            return false
        }
    }

    /**
     * Pre-push divergence resolution: merge, fast-forward, or adopt remote state.
     * Called BEFORE push so the CAS after push succeeds on the first try.
     * This is the key architectural change that eliminates the re-upload loop:
     * by resolving divergence BEFORE uploading 296 MB, we avoid wasting a 13-min
     * push only to discover CAS_MISMATCH afterwards.
     */
    private async syncResolveDivergence(
        remoteHead: string | undefined,
        options: { preferLazyRemoteAdoption?: boolean } = {},
    ): Promise<SyncResolution> {
        const localHead = this.localHead
        if (!localHead || !remoteHead || localHead === remoteHead) return { kind: 'none', touchedConflictState: false }
        const preferLazy = this.shouldPreferLazyRemoteAdoption(options.preferLazyRemoteAdoption === true)

        if (this.canAdoptRemoteWithoutHistoryWalk(localHead)) {
            this.pushSyncLog({
                type: 'sync-start',
                detail: `Adopting remote ${remoteHead.slice(0, 8)} without history walk because local head is the last synced remote head`,
            })
            await this.adoptRemoteHeadForSync(remoteHead, 'Clean stale remote head adoption', preferLazy)
            await this.discardOutboxAfterRemoteAdoption('clean stale remote head adoption')
            return { kind: 'none', touchedConflictState: true }
        }

        const relation = await this.analyzeHeadRelation(localHead, remoteHead)

        if (relation.kind === 'same') {
            return { kind: 'none', touchedConflictState: false }
        }

        if (relation.kind === 'local_ahead') {
            this.pushSyncLog({
                type: 'sync-start',
                detail: `Local ahead of remote by ${relation.replayCommits.length} commit${relation.replayCommits.length === 1 ? '' : 's'} — replaying local history without merge`,
            })
            return {
                kind: 'replay',
                replay: {
                    baseHead: remoteHead,
                    commits: relation.replayCommits,
                },
                touchedConflictState: false,
            }
        }

        if (relation.kind === 'remote_ahead') {
            const lastSnapshot = this.commitSnapshots.get(localHead)
            if (this.hasUncommittedLocalState(lastSnapshot)) {
                this.pushSyncLog({
                    type: 'sync-start',
                    detail: `Fast-forward blocked: uncommitted local edits exist. Falling through to merge.`,
                })
            } else {
                this.pushSyncLog({ type: 'pull-ok', detail: `Fast-forward: local already in ${remoteHead.slice(0, 8)}` })
                await this.adoptRemoteHeadForSync(remoteHead, 'Fast-forward remote head', preferLazy)
                await this.discardOutboxAfterRemoteAdoption('fast-forward')
                return { kind: 'none', touchedConflictState: true }
            }
        }

        if (relation.kind === 'diverged'
            && relation.mergeBase == null
            && this.remoteHead === localHead
            && !this.hasUncommittedLocalState(this.commitSnapshots.get(localHead))) {
            this.pushSyncLog({
                type: 'sync-start',
                detail: `Remote history is beyond local merge window; adopting remote ${remoteHead.slice(0, 8)} because local head was the last synced remote head`,
            })
            await this.adoptRemoteHeadForSync(remoteHead, 'Remote head outside local merge window', preferLazy)
            await this.discardOutboxAfterRemoteAdoption('bounded remote history adoption')
            return { kind: 'none', touchedConflictState: true }
        }

        // Full 3-way merge
        const mergeBase = relation.kind === 'diverged' && relation.mergeBase == null
            ? await this.resolveLastKnownRemoteMergeBase(localHead, remoteHead)
            : relation.mergeBase
        this.pushSyncLog({ type: 'sync-start', detail: `Pre-push merge with ${remoteHead.slice(0, 8)}...` })
        const remoteState = await this.fetchStateStrict(remoteHead, 'Merge remote head')
        await this.mergeStates(remoteState, remoteHead, localHead, mergeBase)
        return { kind: 'none', touchedConflictState: true }
    }

    private canAdoptRemoteWithoutHistoryWalk(localHead: string): boolean {
        return this.remoteHead === localHead
            && !this.hasUncommittedLocalState(this.commitSnapshots.get(localHead))
    }

    private shouldPreferLazyRemoteAdoption(preferLazyRemoteAdoption: boolean): boolean {
        return preferLazyRemoteAdoption && this.syncPolicy === 'on-demand'
    }

    private async resolveLastKnownRemoteMergeBase(localHead: string, remoteHead: string): Promise<string | null> {
        const candidate = this.remoteHead
        if (!candidate || candidate === remoteHead) return null

        try {
            await this.fetchStateStrict(candidate, 'Last-known remote merge-base')
            this.pushSyncLog({
                type: 'sync-start',
                detail: `Using last-known remote ${candidate.slice(0, 8)} as merge base after bounded history search missed the LCA`,
            })
            return candidate
        } catch {
            this.pushSyncLog({
                type: 'sync-start',
                detail: `Last-known remote ${candidate.slice(0, 8)} is unavailable as merge base; falling back to empty-base merge`,
            })
            return null
        }
    }

    private async adoptRemoteHeadForSync(remoteHead: string, purpose: string, preferLazy: boolean): Promise<void> {
        const remoteMeta = await this.parseCommitObject(remoteHead)
        const remoteState = await this.fetchStateStrict(remoteHead, purpose)
        const parents = remoteMeta?.parents ?? []
        const stateHash = remoteMeta?.stateHash

        if (preferLazy && remoteState.rootDirHash) {
            await this.adoptRemoteStateLazy(
                remoteState,
                remoteHead,
                parents,
                stateHash,
                'fast-forward',
            )
            return
        }

        const flatFiles = await this.flattenState(remoteState)
        await this.adoptRemoteState(
            flatFiles,
            remoteHead,
            parents,
            'fast-forward',
        )
    }

    private async discardOutboxAfterRemoteAdoption(reason: string): Promise<void> {
        const discarded = await this.deps.repository.discardOutbox?.(this.blobId) ?? 0
        if (discarded > 0) {
            this.pushSyncLog({
                type: 'sync-start',
                detail: `Discarded ${discarded} stale outbox object${discarded === 1 ? '' : 's'} after ${reason}`,
            })
        }
    }

    private async fetchRemoteState(commitHash: string): Promise<WorkspaceState | null> {
        const commitBytes = await this.readObject(commitHash)
        if (!commitBytes) return null
        const commit = JSON.parse(new TextDecoder().decode(commitBytes))
        return this.decryptStateFromParsedCommit(commit)
    }

    /**
     * §10.1.2 dedup: Decrypt state given an already-parsed commit object.
     * Used by walkCommitChain (avoids re-parsing) and fetchRemoteState.
     */
    private async decryptStateFromParsedCommit(commit: any): Promise<WorkspaceState | null> {
        const stateHash = commit.payload?.stateHash || commit.stateHash
        if (!stateHash) return null

        const stateBytes = await this.readObject(stateHash)
        if (!stateBytes) return null

        try {
            return await decryptState(stateBytes, {
                crypto: this.crypto,
                stateKey: this.stateKey,
                blobId: this.blobId,
            })
        } catch (e) {
            // §7.4.3 AEAD corruption — auth tag mismatch or truncated ciphertext
            sdkConsole.warn(`RealWorkspace: AEAD corruption in remote state for ${this.blobId.slice(0, 12)} — skipping merge`, e)
            return null
        }
    }

    /**
     * Adopt a commit that was pushed by our own pipeline.
     * This avoids a merge cycle when syncNow encounters a CAS_MISMATCH
     * against our own recently-pushed commit.
     */
    /**
     * §7.2.3 adoptRemoteState — consolidated state adoption.
     *
     * Common method used by fast-forward, adoptPipelineCommit, and pullRemoteStateIfNeeded.
     * Handles: clear files → set from state → snapshot → heads → history → emit events.
     */
    private async adoptRemoteState(
        files: MutableFileEntry[],
        commitHash: string,
        parents: string[],
        source: 'fast-forward' | 'adopt' | 'sync',
        updateRemoteHead: boolean = true,
    ): Promise<void> {
        const previousSnapshot = this.captureFileSnapshot()
        this.lastConflictPaths = []

        this.files.clear()
        for (const file of files) {
            this.files.set(file.path, { ...file })
            this.pendingTextCache.delete(file.path)
        }
        const changedPaths = this.collectChangedPaths(previousSnapshot, this.captureFileSnapshot())
        this.localHeadParents = parents
        // §7.4: Track stateHash for GC protection (collectProtectedHashes + buildReferencedObjects)
        try {
            const commitBytes = await this.deps.repository.load(commitHash)
            if (commitBytes) {
                const commitObj = JSON.parse(new TextDecoder().decode(commitBytes))
                this.localHeadStateHash = commitObj.payload?.stateHash || commitObj.stateHash
                // §7.2.3: Track rootDirHash for incremental commits
                const stateHash = commitObj.payload?.stateHash || commitObj.stateHash
                if (stateHash) {
                    try {
                        const stateBytes = await this.deps.repository.load(stateHash)
                        if (stateBytes) {
                            const state = await decryptState(stateBytes, { crypto: this.crypto, stateKey: this.stateKey, blobId: this.blobId })
                            this.currentRootDirHash = state.rootDirHash ?? null
                            this.resetDirTreeCache(this.currentRootDirHash)
                        }
                    } catch { /* best-effort */ }
                }
            }
        } catch { /* best-effort — stateHash tracking is non-critical for adoption */ }
        await this.updateHeads(commitHash, updateRemoteHead ? commitHash : undefined)
        this.commitSnapshots.set(commitHash, this.captureFileSnapshot())
        await this.rebuildHistoryIndex(commitHash)
        this.refreshState()
        this.events.emit('state:changed', this.state)
        // Only emit actually changed paths (not the entire file list)
        if (changedPaths.length > 0) {
            this.events.emit('fs:external-change', {
                paths: changedPaths,
                source,
            })
        }
    }

    private async adoptRemoteStateLazy(
        state: WorkspaceState,
        commitHash: string,
        parents: string[],
        stateHash: string | undefined,
        source: 'fast-forward' | 'adopt' | 'sync',
        updateRemoteHead: boolean = true,
    ): Promise<void> {
        this.lastConflictPaths = []
        this.files.clear()
        this.pendingTextCache.clear()
        this.localHeadParents = parents
        this.localHeadStateHash = stateHash
        this.currentRootDirHash = state.rootDirHash ?? null
        this.resetDirTreeCache(this.currentRootDirHash)
        await this.updateHeads(commitHash, updateRemoteHead ? commitHash : undefined)
        this.commitSnapshots.set(commitHash, this.captureFileSnapshot())
        // On-demand cold-start must not walk historical commits or Merkle trees.
        // History remains empty until an eager sync path has explicitly
        // materialized it.
        this.historyIndex = []
        this.events.emit('history:changed', { newCommits: [] })
        this.refreshState()
        this.events.emit('state:changed', this.state)
        this.events.emit('fs:external-change', {
            paths: ['/'],
            source,
        })
    }

    private async ensureDemandReadableRemoteState(): Promise<void> {
        if (this.closed || this.tombstoned || this.syncPolicy === 'paused') return
        if (this.localHead || this.dirTreeCache || this.currentRootDirHash) return
        if (!resolveNavigatorOnlineForWorkspaceSync()) return

        const { heads, prefetchHints } = await this.getRemoteHeadsWithBootstrapRecovery()
        this.prefetchRemoteHintsIfEager(prefetchHints)

        const remoteHead = heads[0]
        if (!remoteHead) return
        if (this.localHead || this.dirTreeCache || this.currentRootDirHash) return

        const remoteMeta = await this.parseCommitObject(remoteHead)
        const remoteState = await this.fetchStateStrict(remoteHead, 'On-demand path read')
        if (remoteState.deleted) {
            sdkConsole.debug('[RealWorkspace] Peer tombstone detected during on-demand path read')
            this.tombstoned = true
            this.lastSyncError = undefined
            this.close()
            return
        }

        if (remoteState.writers) {
            this.writerStateEntries = [...remoteState.writers]
        }
        this.ensureSelfWriter()

        await this.adoptRemoteStateLazy(
            remoteState,
            remoteHead,
            remoteMeta?.parents ?? [],
            remoteMeta?.stateHash,
            'sync',
        )
        this.pushSyncLog({
            type: 'pull-ok',
            detail: `Initialized on-demand remote root ${remoteHead.slice(0, 8)} for path read`,
        })
    }

    private async adoptPipelineCommit(commitHash: string, updateRemoteHead: boolean = true): Promise<void> {
        const commitBytes = await this.readObject(commitHash)
        if (!commitBytes) return

        const commit = JSON.parse(new TextDecoder().decode(commitBytes))
        const stateHash = commit.payload?.stateHash || commit.stateHash
        if (!stateHash) return

        const stateBytes = await this.readObject(stateHash)
        if (!stateBytes) return

        const state = await decryptState(stateBytes, {
            crypto: this.crypto,
            stateKey: this.stateKey,
            blobId: this.blobId,
        })

        // §5.4.1: Extract writers from adopted commit
        this.extractWritersFromCommit(commit)
        if (state.writers) {
            for (const w of state.writers) {
                const existing = this.writerStateEntries.find(e => e.kid === w.kid)
                if (!existing) {
                    this.writerStateEntries.push(w)
                } else if (w.addedAt > existing.addedAt) {
                    Object.assign(existing, w)
                }
                const kw = this.knownWriters.get(w.kid)
                if (kw) {
                    kw.name = kw.name ?? w.name
                    kw.emoji = kw.emoji ?? w.emoji
                }
            }
        }
        this.ensureSelfWriter()

        const flatFiles = await this.flattenState(state)
        await this.adoptRemoteState(flatFiles, commitHash, commit.parents || [], 'adopt', updateRemoteHead)
    }

    private async mergeStates(
        remoteState: WorkspaceState,
        remoteHead: string,
        oldLocalHead: string,
        mergeBaseHash?: string | null,
        options?: { updateRemoteHead?: boolean },
    ): Promise<void> {
        const updateRemoteHead = options?.updateRemoteHead ?? true

        // §7.7 point 2: Peer-tombstone detection — tombstone always wins over local changes.
        if (remoteState.deleted) {
            sdkConsole.debug('[RealWorkspace] Peer tombstone detected during merge (deleted: true in remote state)')
            this.tombstoned = true
            this.lastSyncError = undefined
            this.close()
            return
        }

        const previousSnapshot = this.captureFileSnapshot()

        // ── Materialize lazy files before building local Merkle tree ──
        // When syncPolicy is 'on-demand', adoptRemoteStateLazy clears this.files
        // and sets up the dirTreeCache for on-demand loading. If the user then
        // makes edits (adding/removing files) without first reading every file,
        // this.files only contains the modified entries. Building the local tree
        // from just those entries would cause the merge to misinterpret missing
        // baseline files as user deletions. Materialize the full file set first.
        //
        // Additionally, entries injected directly into this.files (e.g., by
        // uncommitted dirty writes or optimistic UI edits) may be missing
        // metadata fields like kind/mime/size. Fill those from the committed
        // baseline so the tree builder and content merge see complete entries.
        if (this.dirTreeCache?.isLoaded && this.currentRootDirHash) {
            try {
                const flatStore = this.createDirObjectStore()
                const flatDeps = this.dirTreeDeps()
                const flatFiles = await flattenStateToFiles(this.currentRootDirHash, flatStore, flatDeps)
                for (const f of flatFiles) {
                    if (!this.files.has(f.path)) {
                        if (!this.pendingTextCache.has(f.path)) {
                            this.files.set(f.path, { ...f })
                        }
                    } else {
                        // Merge: fill missing metadata from the committed baseline entry.
                        // This handles dirty entries that were injected with only a
                        // content field (e.g., pendingTextCache bypass), preserving the
                        // modified content while ensuring the tree builder sees a
                        // complete entry with kind, mime, size, etc.
                        const existing = this.files.get(f.path)!
                        if (existing.kind === undefined) existing.kind = f.kind
                        if (!existing.mime && f.mime) existing.mime = f.mime
                        if (!existing.size && f.size) existing.size = f.size
                        if (!existing.mtime && f.mtime) existing.mtime = f.mtime
                        if (!existing.ctime && f.ctime) existing.ctime = f.ctime
                    }
                }
            } catch { /* best-effort — fall through to current entries */ }
        }

        // ── Build local Merkle tree from current in-memory files ──
        const dirDeps = this.dirTreeDeps()
        const store = this.createDirObjectStore()

        // Build tree from this.files → get localRootHash
        const localEntries = [...this.files.entries()].map(([path, f]) => {
            let entry: any
            if (f.kind === 'directory') {
                entry = {
                    kind: 'directory' as const,
                    mtime: f.mtime,
                    ctime: f.ctime ?? f.mtime,
                    meta: f.meta,
                }
            } else if (f.kind === 'symlink') {
                entry = {
                    kind: 'symlink' as const,
                    target: (f as any).target,
                    mtime: f.mtime,
                    ctime: f.ctime ?? f.mtime,
                    meta: f.meta,
                }
            } else {
                entry = {
                    kind: 'file' as const,
                    mime: f.mime,
                    size: f.size,
                    mtime: f.mtime,
                    ctime: f.ctime ?? f.mtime,
                    content: f.content,
                    lastWriterKid: f.lastWriterKid,
                    meta: f.meta,
                }
            }
            return { path, entry }
        })
        const localTree = await buildTreeFromPaths(localEntries as any, dirDeps)

        // Store local tree objects so tree merge can read them
        const tx = this.deps.repository.transaction(this.blobId)
        for (const [hash, bytes] of localTree.objects) tx.put(hash, bytes)
        await tx.commit()

        // ── Get base rootDirHash ──
        // IMPORTANT: merge base must be a common ancestor of oldLocalHead + remoteHead.
        // Using stale this.remoteHead here can misclassify unchanged local text as
        // "both changed" and trigger remote_wins conflict resolution unnecessarily.
        const resolvedMergeBaseHash = mergeBaseHash === undefined
            ? await this.findMergeBaseCommit(oldLocalHead, remoteHead)
            : mergeBaseHash
        if (resolvedMergeBaseHash && resolvedMergeBaseHash !== this.remoteHead) {
            this.pushSyncLog({
                type: 'sync-start',
                detail: `Resolved merge-base ${resolvedMergeBaseHash.slice(0, 8)} for local=${oldLocalHead.slice(0, 8)} remote=${remoteHead.slice(0, 8)}`,
            })
        }
        let baseRootHash: string | null = null
        if (resolvedMergeBaseHash) {
            baseRootHash = await this.loadMergeBaseRootHash(resolvedMergeBaseHash)
        }

        // ── Build TreeMergeIO for content-level merge ──
        // All crypto operations delegated to pipeline — single source of truth for keys/AAD.
        const treeMergeIO: TreeMergeIO = {
            readContentText: async (content) => await this.readContentText(content) || '',
            readContentBytes: async (content) => this.readContentBytes(content),
            readManifestChunks: async (content) => this.pipeline.readManifestChunks(content),
            encodeContent: async (bytes) => this.pipeline.encodeContent(bytes),
            rebuildManifest: async (chunks, baseContent) => this.pipeline.rebuildManifest(chunks, baseContent),
            // §94 §2.3: SQLite page-level merge I/O
            readSqlitePages: async (content) => this.pipeline.readSqlitePages(content),
            encodeSqliteDb: async (pages, pageSize) => this.pipeline.encodeDbPages(pages, pageSize),
            // §94 §3: TB-scale delta I/O
            readSqliteManifest: async (content) => this.pipeline.readSqliteManifest(content),
            readSqlitePagesByPgno: async (content, pgnos) => this.pipeline.readSqlitePagesByPgno(content, pgnos),
            encodeSqliteDeltaPages: async (changed, baseContent, pageSize, nTruncate) =>
                this.pipeline.encodeSqliteDeltaPages(changed, baseContent, pageSize, nTruncate),
        }

        // ── Tree merge ──
        const { mergedRootHash, newDirObjects, conflictPaths, anyLocalChange, mergedFiles } =
            await computeTreeMerge(
                {
                    localRootHash: localTree.rootDirHash,
                    remoteRootHash: remoteState.rootDirHash ?? null,
                    baseRootHash,
                },
                store,
                dirDeps,
                { io: treeMergeIO, conflictPolicy: this.deps.mergeConflictPolicy },
            )
        this.lastConflictPaths = Array.from(new Set(conflictPaths))

        // Store new directory objects from merge
        if (newDirObjects.size > 0) {
            const mergeTx = this.deps.repository.transaction(this.blobId)
            for (const [hash, bytes] of newDirObjects) mergeTx.put(hash, bytes)
            await mergeTx.commit()
        }

        // Log conflicts
        for (const cp of conflictPaths) {
            this.pushSyncLog({
                type: 'pull-ok',
                detail: `Conflict auto-resolved for ${cp}: remote version kept (local preserved in history)`,
            })
        }

        // §5.4.2: Merge writer metadata from remote state (add-wins)
        if (remoteState.writers) {
            for (const rw of remoteState.writers) {
                const localEntry = this.writerStateEntries.find(e => e.kid === rw.kid)
                if (!localEntry) {
                    this.writerStateEntries.push(rw)
                } else if (rw.addedAt > localEntry.addedAt) {
                    Object.assign(localEntry, rw)
                }
                const existing = this.knownWriters.get(rw.kid)
                if (existing) {
                    existing.name = existing.name ?? rw.name
                    existing.emoji = existing.emoji ?? rw.emoji
                } else {
                    this.knownWriters.set(rw.kid, {
                        kid: rw.kid,
                        pk: rw.pk,
                        name: rw.name,
                        emoji: rw.emoji,
                        role: 'editor',
                    })
                }
            }
        }
        this.ensureSelfWriter()

        // Apply merged state — convert FlatFileEntry to MutableFileEntry
        this.files.clear()
        for (const [path, flat] of mergedFiles) {
            this.files.set(path, {
                path,
                kind: 'file' as const,
                mime: flat.entry.mime,
                size: flat.entry.size,
                mtime: flat.entry.mtime,
                ctime: flat.entry.ctime,
                content: flat.entry.content,
                lastWriterKid: flat.entry.lastWriterKid,
                meta: flat.entry.meta as Record<string, unknown> | undefined,
            })
        }

        const affectedPaths = this.collectChangedPaths(previousSnapshot, this.captureFileSnapshot())
        for (const p of affectedPaths) {
            this.pendingTextCache.delete(p)
        }

        // C1 Layer 2: If no local-only content, adopt remote head
        if (!anyLocalChange) {
            this.localHeadParents = [remoteHead]
            await this.updateHeads(remoteHead, updateRemoteHead ? remoteHead : undefined)
            // §7.2.3: Extract rootDirHash from adopted remote state for incremental commits
            try {
                const commitBytes = await this.readObject(remoteHead)
                if (commitBytes) {
                    const commitObj = JSON.parse(new TextDecoder().decode(commitBytes))
                    const sh = commitObj.payload?.stateHash || commitObj.stateHash
                    if (sh) {
                        const stateBytes = await this.readObject(sh)
                        if (stateBytes) {
                            const st = await decryptState(stateBytes, { crypto: this.crypto, stateKey: this.stateKey, blobId: this.blobId })
                            this.currentRootDirHash = st.rootDirHash ?? null
                            this.resetDirTreeCache(this.currentRootDirHash)
                        }
                    }
                }
            } catch { /* best-effort */ }
            this.commitSnapshots.set(remoteHead, this.captureFileSnapshot())
            this.pushSyncLog({ type: 'pull-ok', detail: 'Adopted remote (no local-only changes)' })
            await this.rebuildHistoryIndex(remoteHead)
            this.refreshState()
            this.events.emit('state:changed', this.state)
            this.events.emit('fs:external-change', {
                paths: affectedPaths,
                source: 'merge' as const,
            })
            return
        }

        // Create merge commit with parents=[localHead, remoteHead]
        // §7.2.3: Full rebuild — merged files may differ from either tree
        await this.commitAndEmit([...this.files.keys()], [remoteHead], true)
        if (updateRemoteHead) {
            await this.updateHeads(undefined, remoteHead)
        }

        await this.rebuildHistoryIndex(this.localHead!)

        if (conflictPaths.length > 0) {
            this.pushSyncLog({ type: 'pull-ok', detail: `Merged with ${conflictPaths.length} conflicts`, affectedPaths: conflictPaths })
        } else {
            this.pushSyncLog({ type: 'pull-ok', detail: `Successfully merged remote changes` })
        }

        this.events.emit('fs:external-change', {
            paths: affectedPaths,
            source: 'merge' as const,
        })
    }

    private async analyzeHeadRelation(localHead: string, remoteHead: string, maxDepth = 128): Promise<HeadRelation> {
        if (!localHead || !remoteHead) return { kind: 'diverged', mergeBase: null }
        if (localHead === remoteHead) return { kind: 'same', mergeBase: localHead }

        const localAncestors = await this.collectAncestors(localHead, maxDepth)
        if (localAncestors.has(remoteHead)) {
            const replayCommits = await this.buildReplayCommitChain(remoteHead, localHead, maxDepth)
            if (!replayCommits) {
                throw new EngineException({
                    code: 'COMMIT_INVALID',
                    message: `Unable to build replay path from ${remoteHead.slice(0, 8)} to ${localHead.slice(0, 8)}`,
                    retryable: true,
                })
            }
            return { kind: 'local_ahead', mergeBase: remoteHead, replayCommits }
        }

        const remoteAncestors = await this.collectAncestors(remoteHead, maxDepth)
        if (remoteAncestors.has(localHead)) {
            return { kind: 'remote_ahead', mergeBase: localHead }
        }

        const mergeBase = await this.findMergeBaseCommit(localHead, remoteHead, maxDepth, localAncestors)
        return { kind: 'diverged', mergeBase }
    }

    private async buildReplayCommitChain(ancestorHead: string, descendantHead: string, maxDepth = 128): Promise<string[] | null> {
        if (ancestorHead === descendantHead) return []

        const queue: Array<{ hash: string; depth: number; path: string[] }> = [
            { hash: descendantHead, depth: 0, path: [descendantHead] },
        ]
        const seen = new Set<string>()

        while (queue.length > 0) {
            const current = queue.shift()!
            if (current.depth > maxDepth) continue
            if (seen.has(current.hash)) continue
            seen.add(current.hash)

            const meta = await this.parseCommitObject(current.hash)
            for (const parentHash of meta?.parents ?? []) {
                if (parentHash === ancestorHead) {
                    return current.path.slice().reverse()
                }
                if (!seen.has(parentHash)) {
                    queue.push({
                        hash: parentHash,
                        depth: current.depth + 1,
                        path: [...current.path, parentHash],
                    })
                }
            }
        }

        return null
    }

    private async fetchStateStrict(commitHash: string, purpose: string): Promise<WorkspaceState> {
        const commitBytes = await this.readObject(commitHash)
        if (!commitBytes) {
            throw new EngineException({
                code: 'OBJECT_MISSING',
                message: `${purpose} commit ${commitHash.slice(0, 8)} is unavailable`,
                retryable: true,
            })
        }

        let commit: any
        try {
            commit = JSON.parse(new TextDecoder().decode(commitBytes))
        } catch {
            throw new EngineException({
                code: 'CORRUPT_DATA',
                message: `${purpose} commit ${commitHash.slice(0, 8)} is invalid JSON`,
                retryable: false,
            })
        }

        const stateHash = commit.payload?.stateHash || commit.stateHash
        if (!stateHash) {
            throw new EngineException({
                code: 'COMMIT_INVALID',
                message: `${purpose} commit ${commitHash.slice(0, 8)} is missing stateHash`,
                retryable: false,
            })
        }

        const stateBytes = await this.readObject(stateHash)
        if (!stateBytes) {
            throw new EngineException({
                code: 'OBJECT_MISSING',
                message: `${purpose} state ${stateHash.slice(0, 8)} is unavailable`,
                retryable: true,
            })
        }

        try {
            return await decryptState(stateBytes, {
                crypto: this.crypto,
                stateKey: this.stateKey,
                blobId: this.blobId,
            })
        } catch {
            throw new EngineException({
                code: 'CORRUPT_DATA',
                message: `${purpose} state ${stateHash.slice(0, 8)} could not be decrypted`,
                retryable: false,
            })
        }
    }

    private async loadMergeBaseRootHash(mergeBaseHash: string): Promise<string | null> {
        const baseState = await this.fetchStateStrict(mergeBaseHash, 'Merge-base')
        return baseState.rootDirHash ?? null
    }

    /**
     * Find a common ancestor commit for local/remote heads.
     * Breadth-first (depth-limited) to handle merge commits with multiple parents.
     */
    private async findMergeBaseCommit(
        localHead: string,
        remoteHead: string,
        maxDepth = 128,
        localAncestors?: Set<string>,
    ): Promise<string | null> {
        if (!localHead || !remoteHead) return null
        if (localHead === remoteHead) return localHead

        const knownLocalAncestors = localAncestors ?? await this.collectAncestors(localHead, maxDepth)
        if (knownLocalAncestors.has(remoteHead)) return remoteHead

        const remoteQueue: Array<{ hash: string; depth: number }> = [{ hash: remoteHead, depth: 0 }]
        const seenRemote = new Set<string>()
        while (remoteQueue.length > 0) {
            const current = remoteQueue.shift()!
            if (current.depth > maxDepth) continue
            if (seenRemote.has(current.hash)) continue
            seenRemote.add(current.hash)

            if (knownLocalAncestors.has(current.hash)) return current.hash

            const meta = await this.parseCommitObject(current.hash)
            for (const p of meta?.parents ?? []) {
                if (!seenRemote.has(p)) remoteQueue.push({ hash: p, depth: current.depth + 1 })
            }
        }

        return null
    }

    private async collectAncestors(head: string, maxDepth = 128): Promise<Set<string>> {
        const ancestors = new Set<string>()
        const queue: Array<{ hash: string; depth: number }> = [{ hash: head, depth: 0 }]
        while (queue.length > 0) {
            const current = queue.shift()!
            if (current.depth > maxDepth) continue
            if (ancestors.has(current.hash)) continue
            ancestors.add(current.hash)
            const meta = await this.parseCommitObject(current.hash)
            for (const p of meta?.parents ?? []) {
                if (!ancestors.has(p)) queue.push({ hash: p, depth: current.depth + 1 })
            }
        }
        return ancestors
    }

    private async readContentText(ref: ContentRef): Promise<string | null> {
        const bytes = await this.pipeline.readContent(ref)
        if (!bytes) return null
        return new TextDecoder().decode(bytes)
    }

    private async readContentBytes(ref: ContentRef): Promise<Uint8Array | null> {
        return this.pipeline.readContent(ref)
    }

    /**
     * R1+R2+R6: Re-push all objects for a commit and its ENTIRE parent chain
     * from local IDB to the remote server. Used when server has lost data.
     *
     * Server bootstrap validation (CryptoBlobStoreEndpoints §8.2) requires ALL
     * parent commit objects to exist. After a full server wipe, only pushing the
     * current commit is insufficient — the parent chain must be restored too.
     *
     * Depth-limited to 50 ancestors to prevent infinite loops on corrupt data.
     */
    private async repushLocalObjectGraph(commitHash: string): Promise<void> {
        const objectsToUpload: Array<{ hash: string; data: Uint8Array }> = []
        const visited = new Set<string>()

        const collect = async (hash: string) => {
            if (!hash || visited.has(hash)) return
            visited.add(hash)
            const bytes = await this.readObject(hash)
            if (bytes) {
                objectsToUpload.push({ hash, data: bytes })
            }
        }

        // Walk the commit chain: current commit + all ancestors (depth-limited)
        const MAX_ANCESTOR_DEPTH = 50
        const commitQueue: string[] = [commitHash]
        let depth = 0

        while (commitQueue.length > 0 && depth < MAX_ANCESTOR_DEPTH) {
            const currentHash = commitQueue.shift()!
            if (visited.has(currentHash)) continue
            depth++

            // 1. Commit object
            await collect(currentHash)
            const commitBytes = await this.readObject(currentHash)
            if (!commitBytes) continue

            const commit = JSON.parse(new TextDecoder().decode(commitBytes))

            // 2. State object (from commit)
            const stateHash = commit.payload?.stateHash || commit.stateHash
            if (stateHash) {
                await collect(stateHash)

                // 3. Full state object graph (only for the HEAD commit — ancestors don't need file content)
                if (currentHash === commitHash) {
                    try {
                        const stateBytes = await this.readObject(stateHash)
                        if (stateBytes) {
                            const state = await decryptState(stateBytes, {
                                crypto: this.crypto,
                                stateKey: this.stateKey,
                                blobId: this.blobId,
                            })
                            for (const hash of await this.collectStateObjectHashes(state)) {
                                await collect(hash)
                            }
                        }
                    } catch (err) {
                        sdkConsole.warn('[Recovery] Could not decrypt state for content hash collection:', err)
                    }
                }
            }

            // 4. Queue parent commits for processing
            const parents: string[] = commit.parents ?? []
            for (const parentHash of parents) {
                if (!visited.has(parentHash)) {
                    commitQueue.push(parentHash)
                }
            }
        }

        // 5. Bulk push all collected objects
        if (objectsToUpload.length > 0) {
            this.pushSyncLog({ type: 'sync-start', detail: `Re-pushing ${objectsToUpload.length} objects (${depth} commits) to server` })
            await this.deps.repository.pushObjects(objectsToUpload, { force: true })
        }
    }

    /**
     * R4: Collect hashes that GC must not evict — the current commit's object graph.
     * V3-optimal: walks the Merkle tree to collect ALL referenced CAS objects
     * (dir objects + file content hashes), not just flat file content.
     */
    private async collectProtectedHashes(): Promise<Set<string>> {
        const hashes = new Set<string>()
        if (this.localHead) hashes.add(this.localHead)
        if (this.localHeadStateHash) hashes.add(this.localHeadStateHash)
        for (const p of this.localHeadParents) {
            if (p) hashes.add(p)
        }

        // Walk Merkle tree to collect all dir objects, manifests, and chunks.
        try {
            const state = this.localHead ? await this.fetchRemoteState(this.localHead) : null
            if (state) {
                for (const hash of await this.collectStateObjectHashes(state)) {
                    hashes.add(hash)
                }
            }
        } catch {
            // Fallback: if tree walk fails, protect flat file hashes
            for (const file of this.files.values()) {
                const h = getContentHash(file.content)
                if (h) hashes.add(h)
            }
        }

        return hashes
    }

    /**
     * §7.4.4: Build the referencedObjects array for server-side GC.
     * V3-optimal: walks the Merkle tree to collect ALL CAS object hashes.
     *
     * This list is sent as HEAD metadata so server GC knows what the new live
     * workspace root protects. It must not be reused as a publish visibility
     * barrier; many entries may already be protected by the current server head.
     */
    private async buildReferencedObjectsForHead(head: string | null | undefined): Promise<string[]> {
        const refs = new Set<string>()
        if (!head) return []
        const cached = this.referencedObjectsCache.get(head)
        if (cached) return cached

        let stateHash: string | null = null
        try {
            const commitBytes = await this.readObject(head)
            if (commitBytes) {
                const commit = JSON.parse(new TextDecoder().decode(commitBytes))
                stateHash = commit.payload?.stateHash || commit.stateHash || null
            }
        } catch { /* best-effort */ }
        addReferencedObjectHash(refs, stateHash)

        // Walk Merkle tree for comprehensive object list, including chunk blobs.
        try {
            const state = await this.fetchRemoteState(head)
            if (state) {
                for (const hash of await this.collectStateObjectHashes(state)) {
                    addReferencedObjectHash(refs, hash)
                }
            }
        } catch {
            // Fallback: flat file content hashes
            if (head === this.localHead) {
                for (const file of this.files.values()) {
                    const h = getContentHash(file.content)
                    addReferencedObjectHash(refs, h)
                }
            }
        }

        const result = Array.from(refs)
        if (this.referencedObjectsCache.size >= RealWorkspace.REFERENCED_OBJECTS_CACHE_MAX) {
            const oldest = this.referencedObjectsCache.keys().next().value
            if (oldest) this.referencedObjectsCache.delete(oldest)
        }
        this.referencedObjectsCache.set(head, result)
        return result
    }

    /** Backward-compatible wrapper used by tests and legacy helpers. */
    private async buildReferencedObjects(): Promise<string[]> {
        return this.buildReferencedObjectsForHead(this.localHead)
    }

    /**
     * Collect all CAS objects reachable from a workspace state:
     * directory objects, inline blobs, manifests, and chunk blobs.
     */
    private async collectStateObjectHashes(state: WorkspaceState): Promise<Set<string>> {
        const hashes = new Set<string>()
        if (!state.rootDirHash) return hashes

        const store = this.createDirObjectStore()
        const dirDeps = this.dirTreeDeps()
        for (const hash of await collectTreeHashes(state.rootDirHash, store, dirDeps)) {
            addReferencedObjectHash(hashes, hash)
        }

        for (const file of await flattenStateToFiles(state.rootDirHash, store, dirDeps)) {
            if (file.kind !== 'file') {
                continue
            }

            if (file.content.type === 'inline') {
                addReferencedObjectHash(hashes, file.content.hash)
                continue
            }

            addReferencedObjectHash(hashes, file.content.manifestHash)
            const manifestChunks = await this.pipeline.readManifestChunks(file.content)
            for (const chunk of manifestChunks ?? []) {
                addReferencedObjectHash(hashes, chunk.hash)
            }
        }

        return hashes
    }

    /**
     * §3.3: Build prefetchHints for cold-start optimization.
     * Walks the commit chain from HEAD through objectCache, collecting each
     * commit hash + its stateHash. Limited to 50 ancestors to bound size.
     * The server stores these alongside the HEAD so GET /heads can return them,
     * allowing cold readers to prefetch all needed objects in one batch.
     *
     * Separate from buildReferencedObjects (GC) — prefetchHints include
     * commit hashes which GC must NOT treat as "referenced" to allow compaction.
     */
    private buildPrefetchHintsForHead(head: string | null | undefined): string[] {
        const hints: string[] = []
        let h: string | null = head ?? null
        for (let i = 0; i < 50 && h; i++) {
            const normalizedHead = normalizeObjectHash(h)
            if (normalizedHead) hints.push(normalizedHead)
            const cached = this.objectCache.get(h)
            if (!cached) break
            try {
                const c = JSON.parse(new TextDecoder().decode(cached))
                const sh = c.payload?.stateHash || c.stateHash
                const normalizedStateHash = normalizeObjectHash(sh)
                if (normalizedStateHash) hints.push(normalizedStateHash)
                h = c.parents?.[0] || null
            } catch { break }
        }
        return hints
    }

    /** Backward-compatible wrapper used by tests and legacy helpers. */
    private buildPrefetchHints(): string[] {
        return this.buildPrefetchHintsForHead(this.localHead)
    }

    /**
     * §5.3.1 Genesis commit — create an empty initial commit to establish
     * the creator's writer kid in the first commit's writers[].
     * No scaffold files needed — an empty state is valid.
     */
    async createGenesisCommit(): Promise<void> {
        if (!this.canWrite) return       // read-only → no-op
        if (this.localHead) return       // already has commits → idempotent
        const { commitHash, stateHash: genesisStateHash, rootDirHash: genesisRootDirHash, commitBytes, stateBytes } = await this.pipeline.commit([], null)
        // §3.3: Warm objectCache so buildPrefetchHints() can walk the commit chain
        this.warmObjectCache(commitHash, commitBytes)
        this.warmObjectCache(genesisStateHash, stateBytes)
        this.localHead = commitHash
        this.localHeadStateHash = genesisStateHash
        // §7.2.3: Track root hash for incremental commits
        this.currentRootDirHash = genesisRootDirHash
        this.resetDirTreeCache(this.currentRootDirHash)
        this.refreshState()
        await this.syncNow()
    }

    /**
     * Write a tombstone commit — marks this workspace as permanently deleted.
     * After this commit syncs, all peers will recognize the workspace as deleted.
     */
    async commitTombstone(): Promise<void> {
        this.ensureWriteAccess()

        // Build tombstone state — marks this workspace as permanently deleted
        const tombstoneState: import('./codec/state').WorkspaceState = {
            type: 'secureblob-state-v3',
            rootDirHash: null,
            deleted: true,
        }
        const { encryptState } = await import('./codec/state')
        const { ciphertext: stateBytes, hash: stateHash } = await encryptState(tombstoneState, {
            crypto: this.crypto,
            stateKey: this.stateKey,
            blobId: this.blobId,
        })

        const { buildSignedCommit } = await import('./commitSigning')
        const { heads } = await this.getRemoteHeadsWithBootstrapRecovery()
        const parents = heads[0] ? [heads[0]] : []

        const writerSeed = this.pipeline.getWriterSeed()
        if (!writerSeed) {
            throw new EngineException({
                code: 'FORBIDDEN',
                message: 'Cannot delete: missing writer seed (read-only access)',
                retryable: false,
            })
        }
        const { commitBytes, commitHash } = await buildSignedCommit({
            blobId: this.blobId,
            parents,
            stateHash,
            writerSeed,
            crypto: this.crypto,
            // §5.4 Writer Preservation: include all known writers in tombstone commit
            additionalWriters: [...this.knownWriters.values()]
                .filter(w => w.kid && w.pk) // Only include writers with both kid and pk
                .map(w => ({ alg: 'ed25519' as const, kid: w.kid, pk: w.pk })),
        })

        const tx = this.deps.repository.transaction(this.blobId)
        tx.put(stateHash, stateBytes)
        tx.put(commitHash, commitBytes)

        const casBase = parents.length > 0 ? [parents[0]] : []
        // §7.4.4: Tombstone referencedObjects = only stateHash (no file content)
        const result = await tx.commit({ base: casBase, new: commitHash, referencedObjects: [stateHash] })

        if (!result.ok) {
            const code = result.code ?? 'UNKNOWN'
            const detail = 'message' in result && result.message ? `: ${result.message}` : ''
            throw new EngineException({
                code: code as import('./types').EngineErrorCode,
                message: `Tombstone commit failed (${code})${detail}`,
                retryable: code === 'CAS_MISMATCH' || code === 'NETWORK_ERROR',
            })
        }

        // Signal server-side soft-delete for GC lifecycle (§13)
        try {
            await this.deps.repository.softDeleteBlob(this.blobId)
        } catch (err) {
            sdkConsole.warn('[commitTombstone] softDeleteBlob failed (will be retried by GC):', err)
        }

        this.files.clear()
        this.close()
    }

    close(): void {
        if (this.closed) {
            return
        }

        this.closed = true
        this.emitSdkObserverEvent('workspace', 'info', 'Workspace closed.', {
            blobId: this.blobId,
        })
        // §95 §7.5: Stop heartbeat + release lease on workspace close
        this.stopLeaseHeartbeat()
        if (this.activeLease) {
            this.deps.repository.releaseLease(this.blobId, this.activeLease.leaseId).catch(() => { /* best-effort */ })
            this.activeLease = null
        }
        // §96 sqlite3mt: Clear sync callbacks (databases will be closed by session)
        this.notificationClient?.dispose()
        this.notificationClient = null
        this.sqlite3mtSyncCallbacks.clear()
        this.sqlite3mtHeadSubscriptions.clear()
        this.sqlite3mtLaneToHeadBlobId.clear()
        this.sqlite3mtHeadListeners.clear()
        this.sqlite3mtHeadUnregisteredListeners.clear()
        this.presence.stopHeartbeat()
        this.scheduler.dispose()
        this.crossTabUnsub()
        // §7.5.2: Clear in-memory object cache
        this.objectCache.clear()
        this.referencedObjectsCache.clear()
        // §10.1.3: Remove persisted history index
        try { sessionStorage.removeItem(this.historyStorageKey) } catch { /* no-op */ }
        if (typeof window !== 'undefined') {
            window.removeEventListener('online', this.handleOnline)
            window.removeEventListener('offline', this.handleOffline)
        }
    }

    updateEntryMetadata(entry: Pick<WorkspaceEntry, 'label' | 'emoji'>): void {
        this.deps.entry = {
            ...this.deps.entry,
            label: entry.label,
            emoji: entry.emoji,
        }
        this.label = entry.label
        this.emoji = entry.emoji
        this.refreshState()
        this.events.emit('state:changed', this.state)
    }

    getEntrySnapshot(): WorkspaceEntry {
        return {
            ...this.deps.entry,
            label: this.label,
            emoji: this.emoji,
            canWrite: this.canWrite,
        }
    }

    emitSdkObserverEvent(
        domain: string,
        level: 'debug' | 'info' | 'warn' | 'error',
        message: string,
        data?: unknown,
    ): void {
        emitSdkEvent(this.deps.observers, { domain, level, message, data })
    }

    emitSdkObserverMetric(
        name: string,
        value: number,
        tags?: Record<string, string>,
    ): void {
        emitSdkMetric(this.deps.observers, name, value, tags)
    }

    setSyncMode(mode: 'active' | 'suspended'): void {
        const wasSuspended = this.syncMode === 'suspended'
        this.syncMode = mode
        this.scheduler.setMode(mode)
        if (mode === 'active' && wasSuspended && !this.closed && !this.tombstoned) {
            this.triggerSync()
        }
    }

    /** Called by RealEngineManager when WS notification arrives.
     *  Routed through scheduler debounce to coalesce burst notifications
     *  and prevent the merge→push→head-changed→sync feedback loop. */
    triggerSync(): void {
        if (this.syncPolicy !== 'auto') return
        this.scheduler.scheduleDebouncedSync(() => this.syncNowInternal({ preferLazyRemoteAdoption: true }))
    }

    private applyPollingPolicy(): void {
        if (this.syncPolicy === 'auto') {
            this.scheduler.startPolling(() => this.syncNowInternal({ preferLazyRemoteAdoption: true }), 15_000)
        } else {
            this.scheduler.stopPolling()
        }
    }

    /**
     * §96 sqlite3mt: Trigger sync for a specific sqlite3mt database by its
     * headBlobId (the server-side CAS-head carrier blob).
     *
     * When the lane-aware WsNotificationClient receives a sqlite3mt
     * head-changed event, RealWorkspace routes it to the matching database's
     * internal sync engine (push/reconcile cycle).
     */
    triggerSqlite3MtSync(headBlobId: string): void {
        if (this.syncPolicy !== 'auto') {
            this.pendingSqlite3MtSyncHeadIds.add(headBlobId)
            return
        }
        const callback = this.sqlite3mtSyncCallbacks.get(headBlobId)
        if (callback) callback()
    }

    /** Toggle scheduler between polling and WS-driven mode */
    setNotificationSource(source: 'polling' | 'websocket'): void {
        this.scheduler.setNotificationSource(source)
    }

    /** Self-echo filter: check if head matches our current localHead (§7.1). */
    isOwnPipelineHead(head: string): boolean {
        return head === this.localHead
    }



    private prefetchRemoteHintsIfEager(prefetchHints: readonly string[] | undefined): void {
        if (this.syncPolicy !== 'auto' || !prefetchHints?.length) return
        this.deps.repository.prefetch([...prefetchHints])
    }

    private async pullRemoteStateIfNeeded(preferLazyRemoteAdoption = false): Promise<void> {
        const { heads, prefetchHints } = await this.getRemoteHeadsWithBootstrapRecovery()
        const remoteHead = heads[0]

        this.prefetchRemoteHintsIfEager(prefetchHints)
        if (!remoteHead) {
            this.pushSyncLog({ type: 'pull-skip', detail: 'No remote head on server' })
            return
        }

        if (this.historyIndex.length > 0 && this.historyIndex[0].commitHash === remoteHead) {
            this.pushSyncLog({ type: 'pull-skip', detail: 'Already up to date' })
            return
        }

        const commitBytes = await this.readObject(remoteHead)
        if (!commitBytes) {
            throw new EngineException({
                code: 'NETWORK_ERROR',
                message: `Remote head ${remoteHead} could not be fetched`,
                retryable: true,
            })
        }

        const commit = JSON.parse(new TextDecoder().decode(commitBytes))
        const stateHash = commit.payload?.stateHash || commit.stateHash
        if (!stateHash) {
            throw new EngineException({
                code: 'CORRUPT_DATA',
                message: 'Malformed commit record: missing stateHash',
                retryable: false,
            })
        }

        const stateBytes = await this.readObject(stateHash)
        if (!stateBytes) {
            throw new EngineException({
                code: 'NETWORK_ERROR',
                message: `State object ${stateHash} could not be fetched`,
                retryable: true,
            })
        }

        let state: WorkspaceState
        try {
            state = await decryptState(stateBytes, {
                crypto: this.crypto,
                stateKey: this.stateKey,
                blobId: this.blobId,
            })
        } catch (e) {
            // §7.4.3 AEAD corruption — auth tag mismatch or truncated ciphertext
            throw new EngineException({
                code: 'CORRUPT_DATA',
                message: `AEAD corruption in remote state: ${e instanceof Error ? e.message : 'unknown'}`,
                retryable: false,
            })
        }

        // §7.7 point 2: Peer-tombstone detection.
        // If a collaborator deleted the workspace, the decrypted state has deleted: true.
        // Tombstone wins — close workspace, manager will handle registry removal via trackWorkspace.
        if (state.deleted) {
            sdkConsole.debug('[RealWorkspace] Peer tombstone detected (deleted: true in remote state)')
            this.tombstoned = true
            this.lastSyncError = undefined
            this.close()
            return // Don't apply files — workspace is dead
        }

        // Extract writers from the remote commit and state
        this.extractWritersFromCommit(commit)
        if (state.writers) {
            this.writerStateEntries = [...state.writers]
            for (const sw of state.writers) {
                const existing = this.knownWriters.get(sw.kid)
                if (existing) {
                    if (sw.name && !existing.name) existing.name = sw.name
                    if (sw.emoji && !existing.emoji) existing.emoji = sw.emoji
                    if (sw.pk && !existing.pk) existing.pk = sw.pk
                } else {
                    this.knownWriters.set(sw.kid, {
                        kid: sw.kid,
                        pk: sw.pk,
                        name: sw.name,
                        emoji: sw.emoji,
                        role: 'editor',
                    })
                }
            }
        }
        this.ensureSelfWriter()

        const parents = commit.payload?.parents || commit.parents || []
        const canLazyAdoptPulledHead =
            !this.localHead
            || !this.canWrite
            || this.canAdoptRemoteWithoutHistoryWalk(this.localHead)
        if (this.shouldPreferLazyRemoteAdoption(preferLazyRemoteAdoption) && canLazyAdoptPulledHead) {
            await this.adoptRemoteStateLazy(state, remoteHead, parents, stateHash, 'sync')
            await this.discardOutboxAfterRemoteAdoption('remote pull')
            this.pushSyncLog({
                type: 'pull-ok',
                detail: `Pulled remote state lazily from server for on-demand background sync`,
            })
            return
        }

        // §7.2.3: Explicit/non-lazy pulls use eager adoption so fs.list,
        // fs.stat, and fs.rename keep the materialized snapshot contract.
        // Background on-demand cold pulls return above via lazy adoption.

        // §7.2.3: Consolidated state adoption
        const flatFiles = await this.flattenState(state)
        await this.adoptRemoteState(flatFiles, remoteHead, parents, 'sync')
        await this.discardOutboxAfterRemoteAdoption('remote pull')

        const pulledPaths = flatFiles.map(f => f.path)
        this.pushSyncLog({ type: 'pull-ok', filesAffected: pulledPaths.length, affectedPaths: pulledPaths, detail: `Pulled ${pulledPaths.length} files from server` })
    }

    private async getRemoteHeadsWithBootstrapRecovery(): Promise<{ heads: string[]; prefetchHints?: string[] }> {
        try {
            return await this.deps.repository.getHeads(this.blobId)
        } catch (error) {
            if (!this.shouldBootstrapAfterAuthFailure(error)) throw error
            await this.ensureServerAclBootstrapped()
            return this.deps.repository.getHeads(this.blobId)
        }
    }

    private shouldBootstrapAfterAuthFailure(error: unknown): boolean {
        if (!(error instanceof Error)) return false
        return error.message.includes('ACL_NOT_FOUND')
            || error.message === 'Auth session failed: 404'
            || error.message.includes('Auth session failed: 404 ')
    }

    private async ensureServerAclBootstrapped(): Promise<void> {
        if (!this.bootstrapPromise) {
            this.bootstrapPromise = (async () => {
                const decoded = decodeWorkspaceSecretV2(this.deps.entry.capability)
                if (!decoded?.writerSeed) {
                    throw new EngineException({
                        code: 'FORBIDDEN',
                        message: 'Workspace ACL missing and secret cannot bootstrap server auth.',
                        retryable: false,
                    })
                }

                try {
                    await bootstrapWorkspace(this.deps.entry.apiBaseUrl, decoded)
                } catch (error) {
                    if (error instanceof Error && error.message.includes('ACL_EXISTS')) return
                    throw error
                }
            })().finally(() => {
                this.bootstrapPromise = null
            })
        }

        await this.bootstrapPromise
    }

    /** §7.5.2: readObject with per-session LRU cache */
    private async readObject(hash: string): Promise<Uint8Array | null> {
        // 1. LRU cache hit
        const cached = this.objectCache.get(hash)
        if (cached) {
            // Move to end (LRU refresh)
            this.objectCache.delete(hash)
            this.objectCache.set(hash, cached)
            return cached
        }
        // 2. IDB → Remote (via repository)
        const data = await this.deps.repository.load(hash)
        if (data) {
            // 3. Write-through to LRU
            this.warmObjectCache(hash, data)
        }
        return data
    }

    /**
     * Best-effort object read for historical traversal where a missing object is
     * an expected truncation condition after server-side compaction. Using the
     * batch endpoint avoids noisy browser 404 resource errors for that case.
     */
    private async readObjectQuiet(hash: string): Promise<Uint8Array | null> {
        const cached = this.objectCache.get(hash)
        if (cached) {
            this.objectCache.delete(hash)
            this.objectCache.set(hash, cached)
            return cached
        }

        const batch = await this.deps.repository.loadBatch([hash])
        const data = batch.get(hash) ?? null
        if (data) {
            this.warmObjectCache(hash, data)
        }
        return data
    }

    /** Adapt BlobRepository to DirObjectStore for Merkle tree operations. */
    private createDirObjectStore(): DirObjectStore {
        return {
            get: (hash: string) => this.readObject(hash),
            put: async (hash: string, bytes: Uint8Array) => {
                const tx = this.deps.repository.transaction(this.blobId)
                tx.put(hash, bytes)
                await tx.commit()
            },
            has: async (hash: string) => (await this.readObject(hash)) !== null,
        }
    }

    /** Build DirTreeCodecDeps from workspace crypto/key state. */
    private dirTreeDeps(): DirTreeCodecDeps {
        return { crypto: this.crypto, stateKey: this.stateKey, blobId: this.blobId }
    }

    /** Flatten a v3 WorkspaceState (rootDirHash → flat file list). */
    private async flattenState(state: WorkspaceState): Promise<FlatFileEntry[]> {
        const store = this.createDirObjectStore()
        return flattenStateToFiles(state.rootDirHash, store, this.dirTreeDeps())
    }

    private async materializeFullFileIndex(): Promise<void> {
        if (!this.currentRootDirHash) return
        const store = this.createDirObjectStore()
        const flatFiles = await flattenStateToFiles(this.currentRootDirHash, store, this.dirTreeDeps())
        for (const file of flatFiles) {
            if (this.pendingTextCache.has(file.path)) continue
            this.files.set(file.path, { ...file })
        }
    }


    /**
     * §5.1.1 Batch import inner logic.
     * Phase 1: encode (encrypt) each file, updating VFS optimistically.
     * Phase 2: create ONE commit with all files.
     * Supports dual cancellation: discard (rollback) or keep (partial commit).
     */
    /** Batch-write pending objects to IDB in chunked transactions to avoid blocking Safari */
    private static readonly IDB_FLUSH_CHUNK_SIZE = 50

    private async _flushPendingObjects(pendingObjects: Array<{ hash: string; bytes: Uint8Array }>): Promise<void> {
        if (pendingObjects.length === 0) return

        const CHUNK = RealWorkspace.IDB_FLUSH_CHUNK_SIZE
        for (let i = 0; i < pendingObjects.length; i += CHUNK) {
            const chunk = pendingObjects.slice(i, i + CHUNK)
            const tx = this.pipeline.createTransaction()
            for (const obj of chunk) tx.put(obj.hash, obj.bytes)
            await tx.commit()
            // Yield to main thread between chunks to prevent Safari freeze
            if (i + CHUNK < pendingObjects.length) {
                await new Promise(r => setTimeout(r, 0))
            }
        }
    }

    /** §5.1.1 Microbatch concurrency for encrypt phase */
    private static readonly IMPORT_CONCURRENCY = 4

    private async _importFilesInner(
        files: ImportFileEntry[],
        opts?: ImportFilesOptions,
    ): Promise<ImportFilesResult> {
        // Block syncNow during import — sync will be triggered by commitAndEmit at end
        this.importing = true
        try {
            return await this._importFilesInnerCore(files, opts)
        } finally {
            this.importing = false
            await this.flushQueuedPeerLocalCommit()
        }
    }

    private async _importFilesInnerCore(
        files: ImportFileEntry[],
        opts?: ImportFilesOptions,
    ): Promise<ImportFilesResult> {
        // Track originals for rollback on Cancel & Discard or error
        const originals = new Map<string, MutableFileEntry | undefined>()
        const processedPaths: string[] = []
        let replacedCount = 0
        // Ö1: Collect all encrypted objects for batched IDB write
        const pendingObjects: Array<{ hash: string; bytes: Uint8Array }> = []

        const rollback = () => {
            for (const [path, original] of originals) {
                if (original) this.files.set(path, original)
                else this.files.delete(path)
            }
        }

        try {
            // ── Phase 1: Encode (encrypt + hash) in microbatches ─────
            opts?.onPhase?.('encrypting')
            const CONCURRENCY = RealWorkspace.IMPORT_CONCURRENCY

            // Build dedup index once (O(n)) instead of scanning per-file (O(n²))
            const dedupIndex = new Map<string, ContentRef>()
            for (const existingFile of this.files.values()) {
                if (existingFile.meta?.plaintextHash) {
                    dedupIndex.set(`${existingFile.size}:${existingFile.meta.plaintextHash}`, existingFile.content)
                }
            }

            for (let batchStart = 0; batchStart < files.length; batchStart += CONCURRENCY) {
                // Check for abort BEFORE each microbatch
                if (opts?.signal?.aborted) {
                    const mode = opts.signal.reason === 'keep' ? 'keep' : 'discard'
                    if (mode === 'keep' && processedPaths.length > 0) {
                        await this._flushPendingObjects(pendingObjects)
                        await this.commitAndEmit(processedPaths)
                        return {
                            created: processedPaths.length,
                            replaced: replacedCount,
                            paths: [...processedPaths],
                            aborted: true,
                        }
                    }
                    throw new DOMException('Import cancelled', 'AbortError')
                }

                const batchEnd = Math.min(batchStart + CONCURRENCY, files.length)
                const batch = files.slice(batchStart, batchEnd)

                // Ö2: Encrypt files in parallel within the microbatch
                const batchResults = await Promise.all(batch.map(async (file, idx) => {
                    const { path, content, mime } = file
                    const bytes = typeof content === 'string'
                        ? new TextEncoder().encode(content)
                        : content
                    const resolvedMime = mime
                        ?? (typeof content === 'string' ? 'text/plain' : 'application/octet-stream')

                    const plaintextHash = await this.crypto.sha256(bytes)

                    // O(1) dedup via hash index (replaces O(n) scan)
                    const existingContentRef = dedupIndex.get(`${bytes.byteLength}:${plaintextHash}`)

                    let encodedSize = bytes.byteLength
                    let encodedContent = existingContentRef
                    let objects: Array<{ hash: string; bytes: Uint8Array }> = []

                    if (!encodedContent) {
                        // Ö1: encodeContentNoCommit — encrypt without IDB write
                        const encoded = await this.pipeline.encodeContentNoCommit(bytes, { path })
                        encodedSize = encoded.size
                        encodedContent = encoded.content
                        objects = encoded.objects
                    }

                    return {
                        path, bytes, resolvedMime, plaintextHash,
                        encodedSize, encodedContent, objects,
                        globalIdx: batchStart + idx,
                    }
                }))

                // Apply results sequentially for ordered progress reporting
                for (const r of batchResults) {
                    // Collect objects for batched IDB write
                    pendingObjects.push(...r.objects)

                    // Track original for rollback
                    const existing = this.files.get(r.path)
                    if (!originals.has(r.path)) {
                        originals.set(r.path, existing ? { ...existing } : undefined)
                    }
                    const isReplace = !!existing
                    if (isReplace) replacedCount++

                    // Update in-memory VFS immediately
                    this.files.set(r.path, {
                        ...(existing ?? {}),
                        path: r.path,
                        kind: 'file' as const,
                        mime: r.resolvedMime,
                        size: r.encodedSize,
                        mtime: Date.now(),
                        ctime: existing?.ctime ?? Date.now(),
                        lastWriterKid: this.derivedWriterKid,
                        content: r.encodedContent,
                        meta: { ...(existing?.meta ?? {}), plaintextHash: r.plaintextHash },
                    })
                    // Update dedup index with newly imported file
                    dedupIndex.set(`${r.encodedSize}:${r.plaintextHash}`, r.encodedContent)
                    processedPaths.push(r.path)

                    opts?.onProgress?.({
                        current: r.globalIdx + 1,
                        total: files.length,
                        path: r.path,
                        replaced: isReplace,
                        replacedTotal: replacedCount,
                    })
                }

                // Yield to main thread between batches so Safari can paint
                if (batchStart + CONCURRENCY < files.length) {
                    await new Promise(r => setTimeout(r, 0))
                }
            }

            // ── Final abort check before committing ─────────────────
            if (opts?.signal?.aborted) {
                const mode = opts.signal.reason === 'keep' ? 'keep' : 'discard'
                if (mode === 'keep' && processedPaths.length > 0) {
                    await this._flushPendingObjects(pendingObjects)
                    await this.commitAndEmit(processedPaths)
                    return {
                        created: processedPaths.length,
                        replaced: replacedCount,
                        paths: [...processedPaths],
                        aborted: true,
                    }
                }
                throw new DOMException('Import cancelled', 'AbortError')
            }

            // ── Phase 1.5: Batch-write ALL encrypted objects to IDB ──
            opts?.onPhase?.('committing')
            await this._flushPendingObjects(pendingObjects)

            // ── Phase 2: ONE commit for all files ───────────────────
            await this.commitAndEmit(processedPaths)

            return {
                created: processedPaths.length,
                replaced: replacedCount,
                paths: [...processedPaths],
            }
        } catch (err) {
            // AbortError propagates as-is (rollback already applied for discard)
            rollback()
            throw err
        }
    }

    private ensureWriteAccess(): void {
        if (!this.canWrite) {
            throw new Error('Workspace is read-only')
        }
    }

    private enqueueMutation(action: () => Promise<void>): void {
        this.queuedMutationCount += 1
        this.mutationQueue = this.mutationQueue
            .then(async () => {
                await action()
                // Clear any previous mutation error on success
                if (this.lastSyncError) {
                    this.lastSyncError = undefined
                    this.refreshState()
                    this.events.emit('state:changed', this.state)
                }
            })
            .catch(error => {
                if (error instanceof EngineException) {
                    // CAS_MISMATCH during commit is recoverable — another tab/device
                    // moved the head. Trigger syncNow() which merges and retries.
                    if (error.error.code === 'CAS_MISMATCH') {
                        this.pushSyncLog({
                            type: 'sync-start',
                            detail: 'Commit CAS conflict — triggering merge sync',
                        })
                        this.refreshState()
                        this.events.emit('state:changed', this.state)
                        void this.syncNow().catch(syncErr => {
                            sdkConsole.warn('[RealWorkspace] Auto-recovery sync failed', syncErr)
                        })
                        return
                    }

                    // OBJECT_MISSING during commit — server lost objects.
                    // Trigger syncNow() which has full recovery logic (R5, Engine Spec §7.4).
                    if (error.error.code === 'OBJECT_MISSING') {
                        this.pushSyncLog({
                            type: 'sync-start',
                            detail: 'Commit objects missing on server — triggering recovery sync',
                        })
                        this.refreshState()
                        this.events.emit('state:changed', this.state)
                        void this.syncNow().catch(syncErr => {
                            sdkConsole.warn('[RealWorkspace] OBJECT_MISSING recovery sync failed', syncErr)
                        })
                        return
                    }

                    // BLOB_SOFT_DELETED = workspace permanently deleted server-side.
                    // This is a lifecycle event, not an error.
                    if (error.error.code === 'BLOB_SOFT_DELETED') {
                        sdkConsole.debug('[RealWorkspace] Workspace deleted server-side (BLOB_SOFT_DELETED)')
                        this.tombstoned = true
                        this.lastSyncError = undefined
                        this.close()
                        this.refreshState()
                        this.events.emit('state:changed', this.state)
                        return
                    }

                    this.lastSyncError = error.error
                } else {
                    this.lastSyncError = {
                        code: 'NETWORK_ERROR',
                        message: error instanceof Error ? error.message : String(error),
                        retryable: true,
                    }
                }
                // Only log actual errors — lifecycle events (above) return early
                sdkConsole.error('[RealWorkspace] Mutation failed', error)
                this.refreshState()
                this.events.emit('state:changed', this.state)
            })
            .finally(() => {
                this.queuedMutationCount = Math.max(0, this.queuedMutationCount - 1)
                void this.flushQueuedPeerLocalCommit().catch(error => {
                    sdkConsole.warn('Queued cross-tab peer commit handling failed after mutation drain', error)
                })
            })
    }

    /** @internal test only */
    async __waitForMutations(): Promise<void> {
        await this.mutationQueue
    }

    private async write(path: string, bytes: Uint8Array, mime: string): Promise<void> {
        this.ensureWriteAccess()

        const plaintextHash = await this.crypto.sha256(bytes)

        let existingContentRef: ContentRef | undefined
        for (const existingFile of this.files.values()) {
            if (existingFile.size === bytes.byteLength && existingFile.meta?.plaintextHash === plaintextHash) {
                existingContentRef = existingFile.content
                break
            }
        }

        let encodedSize = bytes.byteLength
        let encodedContent = existingContentRef

        if (!encodedContent) {
            const encoded = await this.pipeline.encodeContent(bytes, { path })
            encodedSize = encoded.size
            encodedContent = encoded.content
        }

        const current = this.files.get(path)
        this.files.set(path, {
            ...(current ?? {}),
            path,
            kind: 'file' as const,
            mime,
            size: encodedSize,
            mtime: Date.now(),
            ctime: current?.ctime ?? Date.now(),
            lastWriterKid: this.derivedWriterKid,
            content: encodedContent,
            meta: { ...(current?.meta ?? {}), plaintextHash },
        })
        await this.commitAndEmit([path])
        // Clear optimistic cache AFTER commit completes — ensures readText()
        // returns correct plain text throughout the entire pipeline (§3.2.2)
        this.pendingTextCache.delete(path)
    }

    /**
     * §7.2.2 Local-first commit: creates a commit purely locally using
     * this.localHead as parent. No server interaction (getHeads, CAS).
     * syncNow() handles all server push and CAS.
     *
     * §7.2.3 Incremental path: uses commitIncremental() to apply only
     * targeted mutations to the Merkle tree — O(k·log n) instead of O(n).
     */
    private async commitAndEmit(paths: string[], additionalParents?: string[], fullRebuild: boolean = false): Promise<void> {
        this.pendingChanges += 1
        this.committing = true
        this.refreshState()
        this.events.emit('state:changed', this.state)

        try {
            const parent = this.historyIndex[0]?.commitHash

            // S1 defense-in-depth: if snapshot cache missed (e.g. page reload, or
            // adoption path didn't populate cache), build snapshot from historyIndex
            // which was populated by walkCommitChain after adoption.
            let previousSnapshot = parent ? this.commitSnapshots.get(parent) : undefined
            if (!previousSnapshot && parent) {
                // Fallback: fetch parent commit's state and build snapshot from it
                try {
                    const parentState = await this.fetchRemoteState(parent)
                    if (parentState) {
                        const parentFlat = await this.flattenState(parentState)
                        previousSnapshot = new Map(
                            parentFlat.map(f => [
                                f.path,
                                { path: f.path, kind: f.kind ?? 'file', mime: f.mime, size: f.size, mtime: f.mtime, ctime: f.ctime ?? f.mtime, lastWriterKid: f.lastWriterKid, content: f.content, meta: f.meta } as SnapshotFile,
                            ])
                        )
                        // Cache for future use
                        this.commitSnapshots.set(parent, previousSnapshot)
                    }
                } catch { /* best-effort — fall through to empty map */ }
            }
            if (!previousSnapshot) previousSnapshot = new Map<string, SnapshotFile>()

            const writerStateEntries = this.writerStateEntries.length > 0 ? this.writerStateEntries : undefined
            const knownWriters = [...this.knownWriters.values()]
                .filter(w => w.kid && w.pk)
                .map(w => ({ alg: 'ed25519' as const, kid: w.kid, pk: w.pk }))

            let commitResult: { commitHash: string; parents: string[]; stateHash: string; rootDirHash: string; commitBytes: Uint8Array; stateBytes: Uint8Array }

            if (fullRebuild) {
                // §7.2.3 Full rebuild path: O(n) via buildTreeFromPaths.
                // Used for merge commits and genesis re-commits where entire tree changes.
                commitResult = await this.pipeline.commit(
                    [...this.files.values()],
                    this.localHead ?? null,
                    additionalParents,
                    writerStateEntries,
                    knownWriters,
                )
            } else {
                // §7.2.3 Incremental path: O(k·log n) via mutateEntry.
                // Used for normal write/delete/rename operations.
                const store = this.createDirObjectStore()
                const dirDeps = this.dirTreeDeps()
                const mutations: Array<{ path: string; op: MutationOp }> = []
                for (const p of paths) {
                    const file = this.files.get(p)
                    if (file) {
                        if (file.kind === 'directory') {
                            let hash = file.dirHash
                            if (!hash) {
                                // Create proper empty dir object for new local directories.
                                const emptyDir = createDirObject([])
                                const encrypted = await encryptDirObject(emptyDir, dirDeps)
                                hash = encrypted.hash
                                await store.put(hash, encrypted.ciphertext)
                            }
                            const name = p.split('/').filter(Boolean).pop() || p
                            mutations.push({ path: p, op: { type: 'upsert', entry: { name, kind: 'directory', mtime: file.mtime, ctime: file.ctime, dirHash: hash } as DirEntryDir } })
                        } else {
                            mutations.push({ path: p, op: { type: 'upsert', entry: this.buildDirEntryFromFile(p, file) } })
                        }
                    } else {
                        mutations.push({ path: p, op: { type: 'delete' } })
                    }
                }

                commitResult = await this.pipeline.commitIncremental(
                    this.currentRootDirHash,
                    mutations,
                    this.localHead ?? null,
                    store,
                    additionalParents,
                    writerStateEntries,
                    knownWriters,
                )
            }

            const { commitHash, parents: commitParents, stateHash: newStateHash, rootDirHash, commitBytes, stateBytes } = commitResult

            // §7.2.3: Track new rootDirHash for next incremental commit
            this.currentRootDirHash = rootDirHash
            this.resetDirTreeCache(this.currentRootDirHash)
            // §3.3: Warm objectCache so buildPrefetchHints() can walk the commit chain
            this.warmObjectCache(commitHash, commitBytes)
            this.warmObjectCache(newStateHash, stateBytes)
            // Track the actual parents from the commit for CAS base in syncNow
            this.localHeadParents = commitParents
            // §7.4.4: Track stateHash immediately so buildReferencedObjects() returns the correct value
            this.localHeadStateHash = newStateHash
            // §7.2.2: Only update localHead — remoteHead stays at last confirmed
            // server head. syncNow will update remoteHead after successful CAS.
            await this.updateHeads(commitHash)

            const currentSnapshot = this.captureFileSnapshot()
            this.commitSnapshots.set(commitHash, currentSnapshot)

            // §7.4.5: LRU cap on commitSnapshots to prevent unbounded memory growth
            if (this.commitSnapshots.size > RealWorkspace.COMMIT_SNAPSHOTS_MAX) {
                // Evict oldest entry (Map preserves insertion order), but never evict HEAD
                for (const key of this.commitSnapshots.keys()) {
                    if (key !== this.localHead) {
                        this.commitSnapshots.delete(key)
                        break
                    }
                }
            }

            const summary: CommitSummary = {
                commitHash,
                parents: commitParents,
                time: Date.now(),
                writerKid: this.derivedWriterKid,
                affectedFiles: this.computeAffectedFiles(previousSnapshot, currentSnapshot),
            }

            this.historyIndex.unshift(summary)
            this.persistHistoryIndex()
            this.pendingChanges = Math.max(0, this.pendingChanges - 1)
            this.refreshState()
            this.events.emit('history:changed', { newCommits: [commitHash] })
            this.events.emit('state:changed', this.state)
            this.events.emit('fs:changed', { paths })

            // T2: Notify other tabs about this commit
            getCrossTabChannel().broadcast({ type: 'commit', blobId: this.blobId, head: commitHash })

            // §7.2.2: Schedule workspace sync to push local commit to server.
            // The pipeline's commit() only stores locally — this triggers the
            // CAS head update so other devices see the changes.
            this.triggerSync()
        } catch (error) {
            this.pendingChanges = Math.max(0, this.pendingChanges - 1)
            throw error
        } finally {
            this.committing = false
            await this.flushQueuedPeerLocalCommit()
        }
    }

    /**
     * §7.2.3: Convert a MutableFileEntry to a DirEntry for Merkle tree mutation.
     */
    private buildDirEntryFromFile(path: string, file: MutableFileEntry): DirEntry {
        const name = path.split('/').filter(Boolean).pop() || path
        if (file.kind === 'symlink') {
            return {
                name,
                kind: 'symlink',
                target: file.target || '',
                mtime: file.mtime,
                ctime: file.ctime,
                lastWriterKid: file.lastWriterKid,
                meta: file.meta,
            } as DirEntrySymlink
        }
        if (file.kind === 'directory') {
            // commitAndEmit handles directories explicitly before calling this function.
            // This guard prevents silent corruption if call paths change in the future.
            throw new Error(`buildDirEntryFromFile: unexpected directory kind for '${path}' — directories must be handled by commitAndEmit`)
        }
        return {
            name,
            kind: 'file',
            mime: file.mime,
            size: file.size,
            mtime: file.mtime,
            ctime: file.ctime,
            content: file.content,
            lastWriterKid: file.lastWriterKid,
            meta: file.meta,
        } as DirEntryFile
    }

    private pushSyncLog(entry: Omit<SyncLogEntry, 'id' | 'timestamp'>): void {
        this.syncLog.push({ ...entry, id: ++this.syncLogCounter, timestamp: Date.now() })
        if (this.syncLog.length > 50) this.syncLog.splice(0, this.syncLog.length - 50)
    }

    private refreshState(): void {
        const navigatorOnline = resolveNavigatorOnlineForWorkspaceSync()

        let connectivity: 'connected' | 'offline' | 'unreachable' = 'connected'
        if (!navigatorOnline) {
            connectivity = 'offline'
        } else if (
            this.lastSyncError?.code === 'NETWORK_ERROR'
            || this.lastSyncError?.code === 'ABORTED'
        ) {
            connectivity = 'unreachable'
        }

        this.state = {
            syncing: this.syncing,
            online: navigatorOnline,
            connectivity,
            synced: !this.syncing && this.pendingChanges === 0 && !this.lastSyncError,
            lastSyncAt: this.lastSyncAt,
            pendingChanges: this.pendingChanges,
            lastSyncError: this.lastSyncError,
            tombstoned: this.tombstoned || undefined,
            localHead: this.localHead,
            remoteHead: this.remoteHead,
            syncLog: [...this.syncLog],
            conflictPaths: [...this.lastConflictPaths],
            // §7.8: Sync progress & control
            // Show progress when actively syncing OR when paused with preserved progress
            syncProgress: (this.syncing || this.syncPaused) ? this.syncProgressData : undefined,
            syncPolicy: this.syncPolicy,
            syncPaused: this.syncPaused || undefined,
        }
    }

    private async updateHeads(localHead?: string, remoteHead?: string): Promise<void> {
        if (localHead !== undefined) this.localHead = localHead
        if (remoteHead !== undefined) this.remoteHead = remoteHead

        // T1 FIX: Single atomic IDB transaction for both local + remote heads
        await this.deps.repository.setHeadsAtomically(this.blobId, {
            ...(localHead !== undefined ? { local: [localHead] } : {}),
            ...(remoteHead !== undefined ? { remote: remoteHead } : {}),
        })

        this.refreshState()
        this.events.emit('state:changed', this.state)
    }

    private captureFileSnapshot(): Map<string, SnapshotFile> {
        return new Map(
            [...this.files.values()].map(file => [
                file.path,
                {
                    path: file.path,
                    kind: file.kind,
                    mime: file.mime,
                    size: file.size,
                    mtime: file.mtime,
                    ctime: file.ctime ?? file.mtime,
                    lastWriterKid: file.lastWriterKid,
                    target: file.target,
                    content: file.content,
                    meta: file.meta,
                },
            ]),
        )
    }

    private collectChangedPaths(
        previous: Map<string, SnapshotFile>,
        current: Map<string, SnapshotFile>,
    ): string[] {
        const changedPaths: string[] = []
        const allPaths = new Set<string>([...previous.keys(), ...current.keys()])

        for (const path of allPaths) {
            const before = previous.get(path)
            const after = current.get(path)

            if (!before || !after) {
                changedPaths.push(path)
                continue
            }

            const contentChanged = this.contentRefKey(before.content) !== this.contentRefKey(after.content)
            const metadataChanged =
                before.kind !== after.kind
                || before.mime !== after.mime
                || before.size !== after.size
                || before.mtime !== after.mtime
                || before.ctime !== after.ctime
                || before.lastWriterKid !== after.lastWriterKid
                || before.target !== after.target
                || JSON.stringify(before.meta ?? null) !== JSON.stringify(after.meta ?? null)

            if (contentChanged || metadataChanged) {
                changedPaths.push(path)
            }
        }

        return changedPaths.sort((a, b) => a.localeCompare(b))
    }

    /**
     * §7.3 Dirty guard: Detect uncommitted local edits.
     *
     * Compares current in-memory files against the last committed snapshot.
     * Returns true if any file has been added, removed, or had its content
     * changed since the snapshot was taken. Used to guard fast-forward
     * adoption paths against silently dropping uncommitted edits.
     *
     * Returns false if snapshot is empty (cold start / page reload) —
     * a reloaded page cannot have uncommitted user edits.
     */
    private hasUncommittedChanges(snapshot: Map<string, SnapshotFile>): boolean {
        // Optimistic text writes live in pendingTextCache until commit succeeds.
        // Treat them as dirty even if the encoded ContentRef hasn't changed yet.
        if (this.pendingTextCache.size > 0) return true
        // Lazy adoption captures an empty snapshot (this.files was cleared).
        // When the snapshot is empty but this.files has entries, those entries
        // are uncommitted local additions that must not be silently dropped.
        const currentSnapshot = this.captureFileSnapshot()
        if (snapshot.size === 0) return currentSnapshot.size > 0
        return this.collectChangedPaths(snapshot, currentSnapshot).length > 0
    }

    private hasUncommittedLocalState(snapshot?: Map<string, SnapshotFile>): boolean {
        if (this.pendingTextCache.size > 0 || this.queuedMutationCount > 0) return true
        return snapshot ? this.hasUncommittedChanges(snapshot) : false
    }

    /** Stable string key for a ContentRef, usable for equality comparison. */
    private contentRefKey(ref: MutableFileEntry['content']): string {
        if (ref.type === 'inline') return ref.hash
        if (ref.type === 'chunked') return `chunked:${ref.manifestHash}`
        return JSON.stringify(ref)
    }

    private computeAffectedFiles(previous: Map<string, SnapshotFile>, current: Map<string, SnapshotFile>) {
        const allPaths = new Set<string>([...previous.keys(), ...current.keys()])
        const affected: CommitSummary['affectedFiles'] = []

        for (const path of allPaths) {
            const before = previous.get(path)
            const after = current.get(path)
            if (!before && after) {
                affected.push({ path, changeType: 'added' })
                continue
            }
            if (before && !after) {
                affected.push({ path, changeType: 'deleted' })
                continue
            }
            if (before && after) {
                const beforeContent = JSON.stringify(before.content)
                const afterContent = JSON.stringify(after.content)
                if (beforeContent !== afterContent || before.mtime !== after.mtime || before.size !== after.size) {
                    affected.push({ path, changeType: 'modified' })
                }
            }
        }

        return affected.sort((a, b) => a.path.localeCompare(b.path))
    }

    private async readTextAtCommit(path: string, commitHash: string, charset?: string): Promise<string | null> {
        // Handle 'HEAD' — read current in-memory file content (§10.1.1)
        if (commitHash === 'HEAD') {
            const file = this.files.get(path)
            if (!file) return null
            const bytes = await this.pipeline.readContent(file.content)
            return bytes ? new TextDecoder(charset ?? 'utf-8').decode(bytes) : null
        }

        // Try in-memory snapshot first (fast path for session commits)
        const snapshot = this.commitSnapshots.get(commitHash)
        if (snapshot) {
            const file = snapshot.get(path)
            if (!file) return null
            const bytes = await this.pipeline.readContent(file.content)
            return bytes ? new TextDecoder(charset ?? 'utf-8').decode(bytes) : null
        }

        // Fall through: load commit object from IDB/remote, decrypt state, find file
        const state = await this.fetchRemoteState(commitHash)
        if (!state) return null
        const flatFiles = await this.flattenState(state)
        const file = flatFiles.find(f => f.path === path)
        if (!file) return null
        const bytes = await this.pipeline.readContent(file.content)
        return bytes ? new TextDecoder(charset ?? 'utf-8').decode(bytes) : null
    }

    private async listDirAtCommit(path: string, commitHash: string): Promise<FileStat[]> {
        const dirPath = normalizeDirPath(path)

        if (commitHash === 'HEAD') {
            return this.fs.listDirAsync(dirPath)
        }

        const snapshot = this.commitSnapshots.get(commitHash)
        // A non-empty snapshot is authoritative (eager path). An empty snapshot
        // comes from adoptRemoteStateLazy which deliberately skips loading files;
        // fall through to fetchRemoteState for correct directory listings.
        if (snapshot && snapshot.size > 0) {
            return this.buildDirectChildListing(snapshot.values(), dirPath)
        }

        const state = await this.fetchRemoteState(commitHash)
        if (!state) return []

        const cache = new DirTreeCache(state.rootDirHash, this.createDirObjectStore(), this.dirTreeDeps())
        cache.loadLazy()

        const entries = await cache.readdirAsync(dirPath)
        if (!entries) return []

        return entries
            .map(entry => ({ ...mutableFromDirEntry(joinDirEntryPath(dirPath, entry), entry) }))
            .sort((a, b) => a.path.localeCompare(b.path))
    }

    /** Read raw bytes at a specific commit — supports binary file restore (§10.1.1) */
    private async readBytesAtCommit(path: string, commitHash: string): Promise<Uint8Array | null> {
        // Handle 'HEAD' — read current in-memory file content
        if (commitHash === 'HEAD') {
            const file = this.files.get(path)
            if (!file) return null
            return this.pipeline.readContent(file.content)
        }

        const snapshot = this.commitSnapshots.get(commitHash)
        if (snapshot) {
            const file = snapshot.get(path)
            if (!file) return null
            return this.pipeline.readContent(file.content)
        }

        const state = await this.fetchRemoteState(commitHash)
        if (!state) return null
        const flatFiles = await this.flattenState(state)
        const file = flatFiles.find(f => f.path === path)
        if (!file) return null
        return this.pipeline.readContent(file.content)
    }

    private buildDirectChildListing(
        files: Iterable<Pick<MutableFileEntry, 'path' | 'kind' | 'mime' | 'size' | 'mtime' | 'ctime' | 'lastWriterKid' | 'target' | 'meta'>>,
        path: string,
    ): FileStat[] {
        const prefix = path === '/' ? '/' : path.replace(/\/+$/, '') + '/'
        const result: FileStat[] = []
        const dirMeta = new Map<string, { mtime: number; ctime: number; explicit?: FileStat }>()

        for (const file of files) {
            if (!file.path.startsWith(prefix)) continue
            const relative = file.path.slice(prefix.length)
            if (relative.length === 0) continue

            const slashIdx = relative.indexOf('/')
            if (slashIdx === -1) {
                result.push({ ...file })
                continue
            }

            const dirName = relative.slice(0, slashIdx)
            const dirPath = prefix + dirName + '/'
            const existing = dirMeta.get(dirPath)

            if (existing) {
                existing.mtime = Math.max(existing.mtime, file.mtime)
                existing.ctime = Math.min(existing.ctime, file.ctime)
                if (file.kind === 'directory' && file.path === dirPath) {
                    existing.explicit = { ...file }
                }
                continue
            }

            dirMeta.set(dirPath, {
                mtime: file.mtime,
                ctime: file.ctime,
                explicit: file.kind === 'directory' && file.path === dirPath ? { ...file } : undefined,
            })
        }

        for (const [dirPath, meta] of dirMeta) {
            result.push(meta.explicit ?? {
                path: dirPath,
                kind: 'directory',
                mime: '',
                size: 0,
                mtime: meta.mtime,
                ctime: meta.ctime,
            })
        }

        return result.sort((a, b) => a.path.localeCompare(b.path))
    }

    /**
     * Parse a commit object to extract metadata (parents, time, writerKid, writers).
     */
    private async parseCommitObject(commitHash: string): Promise<{ parents: string[]; time?: number; writerKid: string; writers: Array<{ kid: string; pk: string }>; stateHash?: string } | null> {
        const bytes = await this.readObject(commitHash)
        if (!bytes) return null
        const commit = JSON.parse(new TextDecoder().decode(bytes))
        return this.extractCommitMeta(commit)
    }

    /**
     * §10.1.2 dedup: Extract commit metadata from an already-parsed commit object.
     * Used by walkCommitChain to avoid double-parsing the commit JSON.
     */
    private extractCommitMeta(commit: any): {
        parents: string[]; time?: number; writerKid: string;
        writers: Array<{ kid: string; pk: string }>;
        stateHash?: string;
    } | null {
        // Extract writer entries from commit (alg, kid, pk)
        const writers: Array<{ kid: string; pk: string }> = []
        if (Array.isArray(commit.writers)) {
            for (const w of commit.writers) {
                if (w.kid && w.pk) {
                    writers.push({ kid: w.kid, pk: w.pk })
                }
            }
        }

        // Side-effect: accumulate into knownWriters
        this.extractWritersFromCommit(commit)

        return {
            parents: commit.parents || [],
            time: typeof commit.time === 'string'
                ? (Number.isNaN(new Date(commit.time).getTime()) ? undefined : new Date(commit.time).getTime())
                : commit.time,
            writerKid: commit.sig?.kid || commit.writerKid || 'anonymous',
            writers,
            stateHash: commit.payload?.stateHash || commit.stateHash,
        }
    }

    /**
     * Rebuild historyIndex by walking the commit chain from the given head.
     * Used after fast-forward, merge, and adopt paths to ensure remote edits
     * appear in the history panel.
     */
    private async rebuildHistoryIndex(headHash: string): Promise<void> {
        const newHistory: CommitSummary[] = []
        await this.prefetchCommitChain(headHash, 50)
        await this.walkCommitChain(headHash, 50, newHistory)
        this.historyIndex = newHistory
        // §10.1.3: Persist to sessionStorage for fast restore after reload
        this.persistHistoryIndex()
        this.events.emit('history:changed', { newCommits: newHistory.map(c => c.commitHash) })
    }

    /** §10.1.3: Identity-namespaced key for sessionStorage */
    private get historyStorageKey(): string {
        return `sb-history-${this.derivedWriterKid}-${this.blobId}`
    }

    /** §10.1.3: Persist historyIndex to sessionStorage */
    private persistHistoryIndex(): void {
        try {
            const key = this.historyStorageKey
            const json = JSON.stringify(this.historyIndex)
            // Cap at 500KB to avoid filling sessionStorage
            if (json.length > 500_000) {
                const truncated = this.historyIndex.slice(0, 20)
                sessionStorage.setItem(key, JSON.stringify(truncated))
            } else {
                sessionStorage.setItem(key, json)
            }
            this.pruneHistoryStorageEntries()
        } catch { /* QuotaExceeded or unavailable — silently ignore */ }
    }

    /**
     * §10.1.3 Eviction: Limit to 5 blob history entries per session.
     * Removes oldest sb-history-* keys (by insertion order) when count exceeds 5.
     */
    private pruneHistoryStorageEntries(): void {
        try {
            const prefix = `sb-history-${this.derivedWriterKid}-`
            const keys: string[] = []
            for (let i = 0; i < sessionStorage.length; i++) {
                const k = sessionStorage.key(i)
                if (k?.startsWith(prefix)) keys.push(k)
            }
            // Keep only the 5 most recent (current entry was just written, so it's last)
            if (keys.length > 5) {
                // Remove oldest entries (sessionStorage insertion order is not guaranteed,
                // but we remove all except the 5 we want to keep)
                const toRemove = keys.slice(0, keys.length - 5)
                for (const k of toRemove) sessionStorage.removeItem(k)
            }
        } catch { /* silent */ }
    }

    /** §10.1.3: Restore historyIndex from sessionStorage */
    private restoreHistoryIndex(): boolean {
        try {
            const key = this.historyStorageKey
            const raw = sessionStorage.getItem(key)
            if (!raw) return false
            const parsed = JSON.parse(raw)
            if (!Array.isArray(parsed) || parsed.length === 0) return false
            // §10.1.3 Invalidation: reject if first commitHash doesn't match localHead
            if (this.localHead && parsed[0].commitHash !== this.localHead) {
                sessionStorage.removeItem(key)
                return false
            }
            this.historyIndex = parsed
            return true
        } catch { return false }
    }

    /**
     * Walk the commit parent chain and build CommitSummary entries.
     * Reuses fetchRemoteState for decryption.
     */

    /**
     * §10.1.2 batch-prefetch: Warm the LRU cache by bulk-loading the commit
     * chain in 2-3 batched network requests instead of N individual ones.
     */
    private async prefetchCommitChain(startHash: string, maxDepth: number): Promise<void> {
        // Pass 1: Fetch commit objects to discover stateHash + parentHash
        const commitHashes: string[] = []
        let hash: string | null = startHash

        // We only know the first hash. Fetch it, parse, follow parent pointers.
        // Collect all commit hashes we can discover from cache first.
        const uncachedCommitHashes: string[] = []
        for (let depth = 0; depth < maxDepth && hash; depth++) {
            if (this.objectCache.has(hash)) {
                // Already cached — parse to follow parent chain
                const bytes = this.objectCache.get(hash)!
                try {
                    const commit = JSON.parse(new TextDecoder().decode(bytes))
                    commitHashes.push(hash)
                    hash = commit.parents?.[0] || null
                } catch { break }
            } else {
                uncachedCommitHashes.push(hash)
                break // Can't follow chain without parsing, need to fetch
            }
        }

        // Fetch uncached commits (may be just the first one or none)
        if (uncachedCommitHashes.length > 0) {
            const results = await this.deps.repository.loadBatch(uncachedCommitHashes)
            for (const [h, data] of results) this.warmObjectCache(h, data)

            // Continue following the chain from where we left off
            const firstFetched = results.get(uncachedCommitHashes[0])
            if (firstFetched) {
                try {
                    let commit = JSON.parse(new TextDecoder().decode(firstFetched))
                    commitHashes.push(uncachedCommitHashes[0])
                    let nextHash: string | null = commit.parents?.[0] || null

                    // Walk the rest of the chain, collecting all commit hashes
                    for (let depth = commitHashes.length; depth < maxDepth && nextHash; depth++) {
                        commitHashes.push(nextHash)
                        if (this.objectCache.has(nextHash)) {
                            const bytes = this.objectCache.get(nextHash)!
                            try {
                                commit = JSON.parse(new TextDecoder().decode(bytes))
                                nextHash = commit.parents?.[0] || null
                            } catch { break }
                        } else {
                            nextHash = null // Will be resolved in batch 1
                        }
                    }
                } catch { /* not a valid commit */ }
            }
        }

        if (commitHashes.length === 0) return

        // Batch 1: Fetch all commit objects we haven't cached yet
        const missingCommits = commitHashes.filter(h => !this.objectCache.has(h))
        if (missingCommits.length > 0) {
            const batch1 = await this.deps.repository.loadBatch(missingCommits)
            for (const [h, data] of batch1) this.warmObjectCache(h, data)

            // Now we may have discovered more parent hashes — follow the chain further
            let lastFetchedHash = missingCommits[missingCommits.length - 1]
            const lastFetched = this.objectCache.get(lastFetchedHash)
            if (lastFetched) {
                try {
                    let commit = JSON.parse(new TextDecoder().decode(lastFetched))
                    let nextHash: string | null = commit.parents?.[0] || null
                    while (nextHash && commitHashes.length < maxDepth) {
                        if (commitHashes.includes(nextHash)) break
                        commitHashes.push(nextHash)
                        if (this.objectCache.has(nextHash)) {
                            const bytes = this.objectCache.get(nextHash)!
                            try {
                                commit = JSON.parse(new TextDecoder().decode(bytes))
                                nextHash = commit.parents?.[0] || null
                            } catch { break }
                        } else {
                            nextHash = null
                        }
                    }
                } catch { /* ignore */ }
            }
        }

        // Pass 2: Extract stateHash + parentHash from all cached commits, fetch missing
        const secondaryHashes: string[] = []
        for (const ch of commitHashes) {
            const bytes = this.objectCache.get(ch)
            if (!bytes) continue
            try {
                const commit = JSON.parse(new TextDecoder().decode(bytes))
                const stateHash = commit.payload?.stateHash || commit.stateHash
                if (stateHash && !this.objectCache.has(stateHash)) {
                    secondaryHashes.push(stateHash)
                }
                const parentHash = commit.parents?.[0]
                if (parentHash && !this.objectCache.has(parentHash) && !commitHashes.includes(parentHash)) {
                    secondaryHashes.push(parentHash)
                }
            } catch { /* not a commit */ }
        }

        if (secondaryHashes.length > 0) {
            const batch2 = await this.deps.repository.loadBatch(secondaryHashes)
            for (const [h, data] of batch2) this.warmObjectCache(h, data)

            // Pass 3: Any newly fetched parent commits may have state hashes we need
            const tertiaryHashes: string[] = []
            for (const [h, data] of batch2) {
                try {
                    const commit = JSON.parse(new TextDecoder().decode(data))
                    const stateHash = commit.payload?.stateHash || commit.stateHash
                    if (stateHash && !this.objectCache.has(stateHash)) {
                        tertiaryHashes.push(stateHash)
                    }
                } catch { /* not a commit — it's a state object, skip */ }
            }
            if (tertiaryHashes.length > 0) {
                const batch3 = await this.deps.repository.loadBatch(tertiaryHashes)
                for (const [h, data] of batch3) this.warmObjectCache(h, data)
            }
        }
    }

    /** Warm the LRU object cache with a known hash→data pair (§7.5.2). */
    private warmObjectCache(hash: string, data: Uint8Array): void {
        if (this.objectCache.has(hash)) return
        if (this.objectCache.size >= RealWorkspace.OBJECT_CACHE_MAX) {
            const oldest = this.objectCache.keys().next().value!
            this.objectCache.delete(oldest)
        }
        this.objectCache.set(hash, data)
    }

    private async walkCommitChain(
        startHash: string,
        maxDepth: number,
        out: CommitSummary[],
    ): Promise<void> {
        let currentHash: string | null = startHash
        // One-entry state cache: parent state from iteration N is reused as
        // currentState in iteration N+1 (since currentHash advances to parentHash).
        // This reduces decryptions from 2N-1 back to N for a chain of N commits.
        let cachedParent: { hash: string; state: WorkspaceState } | null = null

        for (let depth = 0; depth < maxDepth && currentHash; depth++) {
            let currentState: WorkspaceState | null = null

            // Reuse cached parent state if it matches this iteration's commit hash
            if (cachedParent && cachedParent.hash === currentHash) {
                currentState = cachedParent.state
                cachedParent = null
            }

            // §10.1.2 dedup: Single readObject + single JSON.parse per commit.
            // extractCommitMeta + decryptStateFromParsedCommit both use the same parsed object.
            const commitBytes = await this.readObjectQuiet(currentHash)
            if (!commitBytes) {
                // R3: Parent commit not available (server data loss or GC eviction)
                sdkConsole.debug(`[History] Commit ${currentHash} not available — history truncated`)
                break
            }
            const commit = JSON.parse(new TextDecoder().decode(commitBytes))
            const meta = this.extractCommitMeta(commit)
            if (!meta) break

            if (!currentState) {
                currentState = await this.decryptStateFromParsedCommit(commit)
                if (!currentState) break
            }

            // §10.1.2: Enrich writerStateEntries from decrypted state so enrichCommit can resolve names
            if (currentState.writers) {
                for (const w of currentState.writers) {
                    const existing = this.writerStateEntries.find(e => e.kid === w.kid)
                    if (!existing) {
                        this.writerStateEntries.push(w)
                    } else if (w.name && !existing.name) {
                        existing.name = w.name
                        existing.emoji = existing.emoji ?? w.emoji
                    }
                    // Enrich knownWriters with persisted metadata
                    const kw = this.knownWriters.get(w.kid)
                    if (kw) {
                        kw.name = kw.name ?? w.name
                        kw.emoji = kw.emoji ?? w.emoji
                    }
                }
            }

            // Compute affected files by fetching this commit's ACTUAL parent state.
            // This correctly attributes the diff to the commit that made the change,
            // matching the .NET SDK's approach: diff(parentState, currentState).
            let parentState: WorkspaceState | null = null
            const parentHash = meta.parents[0]
            if (parentHash) {
                const parentCommitBytes = await this.readObjectQuiet(parentHash)
                if (parentCommitBytes) {
                    const parentCommit = JSON.parse(new TextDecoder().decode(parentCommitBytes))
                    parentState = await this.decryptStateFromParsedCommit(parentCommit)
                    // Cache for next iteration (parentHash becomes next currentHash)
                    if (parentState) {
                        cachedParent = { hash: parentHash, state: parentState }
                    }
                }
            }

            const affectedFiles: CommitSummary['affectedFiles'] = await this.computeAffectedFilesFromStates(
                parentState ?? { type: 'secureblob-state-v3', rootDirHash: null },
                currentState,
            )

            out.push({
                commitHash: currentHash,
                parents: meta.parents,
                time: meta.time,
                writerKid: meta.writerKid,
                affectedFiles,
            })

            currentHash = parentHash || null
        }
    }

    /**
     * Compare two WorkspaceStates to determine affected files.
     */
    private async computeAffectedFilesFromStates(
        olderState: WorkspaceState,
        newerState: WorkspaceState,
    ): Promise<CommitSummary['affectedFiles']> {
        const store = this.createDirObjectStore()
        const deps = this.dirTreeDeps()
        const diffs = await diffMerkleTrees(olderState.rootDirHash, newerState.rootDirHash, store, deps)
        return diffs.map(d => ({ path: d.path, changeType: d.changeType }))
    }

    /**
     * Compute blame for a file by walking the commit DAG backward.
     * Implements spec §10.3.1: lazy progressive blame with content-hash dedup
     * and early termination.
     */
    private async computeBlame(path: string, maxDepth = 50): Promise<BlameLine[]> {
        if (!this.localHead) return []

        // Read current text at HEAD
        const headText = await this.readTextAtCommit(path, this.localHead)
        if (headText === null) return []

        const headLines = headText.split('\n')
        if (headLines.length === 0) return []

        // Get HEAD commit metadata
        const headMeta = await this.parseCommitObject(this.localHead)
        if (!headMeta) return []

        // Initialize: all lines attributed to HEAD commit
        const blame: BlameLine[] = headLines.map((text, idx) => ({
            lineNumber: idx + 1,
            text,
            commitHash: this.localHead!,
            writerKid: headMeta.writerKid,
            timestamp: headMeta.time,
        }))

        // Track which HEAD line indices still need attribution
        const unattributed = new Set<number>(headLines.map((_, i) => i))

        // Mapping from currentLines index → headLines index.
        // At depth 0, currentLines === headLines so this is identity.
        let currentToHead = new Map<number, number>(headLines.map((_, i) => [i, i]))

        // Walk backward through parent chain
        let currentHash = this.localHead
        let currentLines = headLines
        let currentState: WorkspaceState | null = null

        for (let depth = 0; depth < maxDepth && unattributed.size > 0; depth++) {
            const meta = await this.parseCommitObject(currentHash)
            if (!meta) break

            const parentHash = meta.parents[0]
            if (!parentHash) break // Reached root commit

            // Content-hash dedup
            if (!currentState) {
                currentState = await this.fetchRemoteState(currentHash)
            }
            const parentState = await this.fetchRemoteState(parentHash)
            if (!parentState) {
                // R3: Parent state not available — blame attribution truncated
                sdkConsole.debug(`[Blame] Parent ${parentHash.slice(0, 8)} state not available — blame truncated`)
                break
            }

            const currentFlat = currentState ? await this.flattenState(currentState) : []
            const parentFlat = await this.flattenState(parentState)

            const currentFile = currentFlat.find(f => f.path === path)
            const parentFile = parentFlat.find(f => f.path === path)

            if (currentFile && parentFile) {
                const currentContentHash = getContentHash(currentFile.content)
                const parentContentHash = getContentHash(parentFile.content)

                if (currentContentHash === parentContentHash) {
                    currentHash = parentHash
                    currentState = parentState
                    continue
                }
            }

            if (!parentFile) break

            // Read parent version and diff
            const parentText = await this.readTextAtCommit(path, parentHash)
            if (parentText === null) break
            const parentLines = parentText.split('\n')

            // Get parent commit metadata
            const parentMeta = await this.parseCommitObject(parentHash)
            if (!parentMeta) break

            // Diff: map newIndex (in currentLines) → oldIndex (in parentLines)
            const mapping = computeLineMapping(parentLines, currentLines)

            // Build new mapping: parentLines index → headLines index
            const nextCurrentToHead = new Map<number, number>()

            for (const m of mapping) {
                const headIdx = currentToHead.get(m.newIndex)
                if (headIdx === undefined) continue // line in current not tracked

                if (m.oldIndex != null) {
                    // Line exists in parent — re-attribute to parent and track further
                    if (unattributed.has(headIdx)) {
                        blame[headIdx] = {
                            ...blame[headIdx],
                            commitHash: parentHash,
                            writerKid: parentMeta.writerKid,
                            timestamp: parentMeta.time,
                        }
                        nextCurrentToHead.set(m.oldIndex, headIdx)
                    }
                } else {
                    // Line is new in currentHash — finalize attribution, remove from unattributed
                    unattributed.delete(headIdx)
                }
            }

            // Move deeper
            currentHash = parentHash
            currentLines = parentLines
            currentState = parentState
            currentToHead = nextCurrentToHead
        }

        // Enrich blame lines with resolved writer names from knownWriters
        for (const line of blame) {
            if (line.writerKid) {
                const w = this.knownWriters.get(line.writerKid)
                if (w?.name) line.writerName = w.name
                if (w?.emoji) line.writerEmoji = w.emoji
            }
        }

        return blame
    }


}

export function createRealWorkspace(
    entry: WorkspaceEntry,
    primaryKid: string,
    localStore?: LocalStore,
    identityProvider?: RealWorkspaceDeps['identityProvider'],
    createSqliteEngineFactory?: WorkspaceSqliteEngineFactoryProvider['createSqliteEngineFactory'],
    useSqlite3Mt?: Sqlite3MtOptions,
    authProvider?: WorkspaceAuthProvider,
    observers?: SdkObservers,
    logging?: SdkLoggingOptions,
): Workspace {
    // Brist A fix: best-effort persistent storage so the browser grants a larger
    // quota and does not evict IDB data under disk pressure. Fire-and-forget;
    // failures are swallowed inside the helper.
    void ensurePersistentBrowserStorage()
    // Per-identity IDB scoping: each identity gets its own IndexedDB database
    const dbName = `secureblob-${primaryKid}`
    const local = typeof indexedDB === 'undefined' ? new MemoryStoreBackend() : new IdbStoreBackend(dbName)
    const resolvedAuthProvider = authProvider ?? createWorkspaceAuthProvider(entry.apiBaseUrl, entry.capability, entry.blobId) ?? undefined
    const remote = new HttpStoreBackend(entry.apiBaseUrl, entry.capability, entry.blobId, resolvedAuthProvider)
    const repository = new BlobRepositoryImpl(local, remote)

    return new RealWorkspace({
        entry,
        repository,
        authProvider: resolvedAuthProvider,
        localBackend: local as IdbStoreBackend,
        identityProvider,
        createSqliteEngineFactory,
        useSqlite3Mt,
        observers,
        logging,
    })
}

export function resolveNavigatorOnlineForWorkspaceSync(
    navigatorLike: { readonly onLine?: boolean } | undefined = typeof navigator === 'undefined' ? undefined : navigator,
): boolean {
    return navigatorLike?.onLine === false ? false : true
}

/**
 * Create AND initialize a workspace — SDK convenience factory.
 *
 * Unlike createRealWorkspace() (which returns synchronously and requires
 * a separate initialize() call), this async function returns a fully-ready
 * workspace with heads loaded, writer kid derived, and initial sync started.
 *
 * This is the preferred entry point for SDK consumers.
 */
export async function openSdkWorkspace(
    entry: WorkspaceEntry,
    primaryKid: string = 'sdk-user',
    createSqliteEngineFactory?: WorkspaceSqliteEngineFactoryProvider['createSqliteEngineFactory'],
    useSqlite3Mt?: Sqlite3MtOptions,
    observers?: SdkObservers,
    logging?: SdkLoggingOptions,
): Promise<Workspace> {
    const workspace = createRealWorkspace(
        entry,
        primaryKid,
        undefined,
        undefined,
        createSqliteEngineFactory,
        useSqlite3Mt,
        undefined,
        observers,
        logging,
    ) as RealWorkspace
    await workspace.initialize()
    return workspace
}
